/**
 * Stammdaten (Creator, Marken, Einladungen, Insights): reine Anzeige- und Rechenlogik ohne Datenbank.
 * Umsätze kommen aus derselben Rechnung wie die Übersicht (dealOccurrences aus @talentkontor/domain),
 * Rechnungszustände aus model/invoice-view. Alles hier ist ohne Browser testbar.
 */
import {
  addMonths, checkIssuerProfile, checkVatIdFormat, COUNTRIES, dealOccurrences, diffDays, isDealActive, isEuCountry, isQrIban, isValidBic, isValidIban, lastDayOfMonth,
  fxMonthConverter, monthRange, normalizeCountry, overviewDealFromRow, overviewInvoiceFromRow, overviewPeriodFromRow, zoneOfCountry, type DealCatalog, type DealMonth,
  type DealRowForOverview, type FxRateRow, type Occurrence, type OrgConfig, type OverviewFee, type OverviewInput, type PeriodRowForOverview,
  type ProfileField,
  DEAL_MODEL_KEYS, dealModelName, csvCellGuard, mailHtmlToPlainText, mailVariables, renderMailText,
} from "@talentkontor/domain";
import type { AgencyTermsRow, BrandRow, DealRow, InvoiceRow, LineItemRow, OrgSettingsRow, PeriodRow, TalentRow } from "../../../data/queries";
import { viewInvoice, type InvoiceView, type Pill, type Tone, holidayRegionOf } from "../../../model/invoice-view";
import { balanceCents } from "../../../model/invoice-activity";
import { monthLabel, numberLocale } from "../../../lib/format";
import { compareFromDaily, type ShareResponse } from "../../oeffentlich/insights-model";
import { t, ta, lazyRecord, currentLang } from "../../../lib/sprache";

// ── Zeilen der Datenbank (Spalten, die die Stammdaten-Seiten brauchen) ──────────────────────────────

export interface TalentFull extends TalentRow {
  street: string | null;
  postal_code: string | null;
  vat_id: string | null;
  tax_number: string | null;
  nickname: string | null;
  tiktok_handle: string | null;
  youtube_handle: string | null;
  whatsapp_phone: string | null;
  whatsapp_opt_in_at: string | null;
  ig_enabled: boolean;
  updated_at: string;
  /** 0042: Sprache für Einladung und Mails, Firma, Geburtsdatum und Erziehungsberechtigte */
  language?: string | null;
  company_name?: string | null;
  legal_form?: string | null;
  register_info?: string | null;
  birth_date?: string | null;
  guardian_name?: string | null;
  guardian_email?: string | null;
  /** 0059: zweite erziehungsberechtigte Person (Verträge mit Minderjährigen) */
  guardian2_name?: string | null;
  guardian2_email?: string | null;
}
export interface BrandFull extends BrandRow {
  brand_number: number | null;
  street: string | null;
  postal_code: string | null;
  city: string | null;
  vat_id: string | null;
  is_business: boolean;
  kind: "brand" | "agency";
  represented_by: string | null;
  invoice_via_agency: boolean;
  /** Abrechnungsweg für Kooperationen mit dieser Marke (0039); leer = Standard der Agentur, die Kooperation kann abweichen */
  invoice_route?: "via_agency" | "direct_to_brand" | null;
  register_info: string | null;
  default_currency: string | null;
  updated_at: string;
}
export interface DealFull extends DealRow {
  products_flat_taxed: boolean;
  link: string | null;
  usage_rights_until: string | null;
  visible_to_talent: boolean;
  updated_at: string;
}
export interface LinkRow { talent_id: string; org_id: string; user_id: string; created_at: string }
export interface PresenceRow { talent_id: string; org_id: string; last_seen_at: string | null; last_view: string | null; last_login_at: string | null }
export interface InvitationRow {
  id: string; org_id: string; kind: string; email: string; talent_id: string | null;
  expires_at: string; accepted_at: string | null; revoked_at: string | null; created_at: string; invited_by: string | null;
}
export interface ContractRow {
  id: string; org_id: string; deal_id: string | null; talent_id: string | null; brand_id: string | null; title: string; status: string;
  contract_number: number | null; sent_at: string | null; completed_at: string | null; voided_at: string | null; created_at: string; origin: string | null;
}
export interface BookingRow {
  id: string; org_id: string; deal_id: string | null; period_id: string | null; talent_id: string | null; brand_id: string | null; invoice_id: string | null;
  kind: string; booked_on: string; amount_cents: number; currency: string; talent_share_cents: number | null; agency_share_cents: number | null;
  visible_to_talent: boolean; status: string; source: string; note: string | null; created_at: string;
}
export interface PayoutRow {
  talent_id: string; org_id: string; iban: string | null; bic: string | null; bank_account_holder: string | null; bank_name: string | null;
  bank_country: string | null; updated_at: string;
  /** 0042: Auszahlungsweg (leer = SEPA), Kontonummer und Routing außerhalb von IBAN-Ländern, PayPal/Wise-Adresse, Auszahlungswährung */
  payout_method?: string | null; account_number?: string | null; routing_code?: string | null; payout_email?: string | null; payout_currency?: string | null;
}
export interface NoteRow { note: string; updated_at: string }
export interface ContactRow {
  id: string; org_id: string; brand_id: string; name: string; email: string | null; phone: string | null; role: string | null;
  is_invoice_recipient: boolean; created_at: string;
  /** 0042: Zuständigkeiten (Rechnung, Rechnung in Kopie, Vertrag, Briefing, Insights); „Rechnung“ entspricht is_invoice_recipient */
  roles?: string[] | null;
}
export type Metrics = Record<string, number | null>;
export interface IgAccountRow {
  talent_id: string; org_id: string; username: string; name: string | null; followers_count: number | null; media_count: number | null;
  status: string; connected_at: string; last_sync_at: string | null; last_error: string | null;
}
export interface IgPeriodRow { talent_id: string; period: string; computed_on: string; metrics: Metrics }
export interface IgAudienceRow { talent_id: string; captured_on: string; breakdown: string; data: Metrics; timeframe?: string | null }
/** 0340: Tageswerte (Vergleich zum Vorzeitraum in der Vorschau) und Storys einzeln */
export interface IgDailyRow { talent_id: string; day: string; metrics: Metrics }
export interface IgStoryRow {
  talent_id: string; story_id: string; media_type: string | null; posted_at: string; metrics: Record<string, unknown>; few_viewers: boolean;
  measured_at: string | null; measurements: number;
}
export interface IgMonthlyRow { talent_id: string; month: string; metrics: Metrics }
export interface IgMediaRow {
  media_id: string; talent_id: string; media_type: string | null; permalink: string | null; caption: string | null; posted_at: string | null; metrics: Metrics;
  /** 0164: Vorschaubild im Bucket ig-media; die Freigabe an die Marke liefert es als thumb (0340), die Vorschau braucht es ebenso */
  thumbnail_path?: string | null;
}
export interface IgConsentRow { talent_id: string; org_id: string; sharing_allowed: boolean; allowed_blocks: string[]; updated_at: string }
export interface IgShareRow {
  id: string; org_id: string; talent_id: string; brand_id: string | null; recipient_email: string; blocks: string[]; period: string;
  expires_at: string; revoked_at: string | null; failed_attempts: number; locked_at: string | null; sent_at: string | null; sent_count: number;
  created_by: string | null; created_at: string;
}
export interface IgShareViewRow { id: number; share_id: string; at: string; ok: boolean }
/** Tarif der Agentur. `maxTalents` und Module sind wirksam (im Test im vollen Umfang, K-377), `planMaxTalents` gilt danach. */
export interface PlanInfo { planKey: string; planName: string; maxTalents: number; moduleInsights: boolean; moduleWhatsapp: boolean; planMaxTalents?: number; testBis?: string | null }

// ── Status als Pille (Zeichen und Text, Zustände aus dem Design-Paket 03_tokens/status.json) ───────

const SYMBOL: Record<Tone, string> = { neutral: "○", info: "◇", warning: "◷", success: "✓", error: "!" };
/**
 * Zahlungsziel einer Marke ohne eigene Vorgabe: dieselbe Kette wie app.brand_payment_terms und der Rechnungsgenerator
 * (Einstellung „Zahlungsziel für Marken“, sonst allgemeines Zahlungsziel; SAAS-R3-1).
 */
export function brandTermsFallback(settings: { brand_payment_terms_days?: unknown; payment_terms_days?: unknown } | null | undefined): number | null {
  const n = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : null);
  return n(settings?.brand_payment_terms_days) ?? n(settings?.payment_terms_days);
}

export function pill(label: string, tone: Tone): Pill {
  return { label, tone, symbol: SYMBOL[tone] };
}

export const DEAL_STATUS: Record<string, readonly [string, Tone]> = {
  get planning() { return [t("In Planung", "In planning"), "neutral"] as const; },
  get contract() { return [t("Vertrag zur Unterschrift", "Contract awaiting signature"), "info"] as const; },
  get concept() { return [t("Konzept ausstehend (Creator)", "Concept pending (creator)"), "warning"] as const; },
  get production() { return [t("In Produktion", "In production"), "info"] as const; },
  get content_approval() { return [t("Freigabe Content", "Content approval"), "warning"] as const; },
  get published() { return [t("Veröffentlicht", "Published"), "success"] as const; },
  reporting: ["Reporting", "info"],
  get invoicing() { return [t("Rechnung ausstehend", "Invoice pending"), "warning"] as const; },
  get done() { return [t("Abgeschlossen", "Completed"), "success"] as const; },
  get blocked() { return [t("Blockiert", "Blocked"), "error"] as const; },
  get cancelled() { return [t("Storniert", "Cancelled"), "neutral"] as const; },
};
export function dealStatusPill(d: Pick<DealRow, "status" | "archived_at">): Pill {
  if (d.archived_at) return pill(t("Archiviert", "Archived"), "neutral");
  const s = DEAL_STATUS[d.status];
  return s ? pill(s[0], s[1]) : pill(d.status, "neutral");
}

/** Modellnamen aus dem gemeinsamen Katalog (deal-models.ts, 0047). */
export const DEAL_MODEL: Record<string, string> = lazyRecord(DEAL_MODEL_KEYS, (k) => dealModelName(k, currentLang()));
export const PLATFORM: Record<string, string> = {
  instagram: "Instagram", tiktok: "TikTok", youtube: "YouTube", ugc: "UGC", podcast: "Podcast", get other() { return t("Sonstige", "Other"); },
};
export function platformsLabel(list: readonly string[] | null | undefined): string {
  return (list ?? []).map((p) => PLATFORM[p] ?? p).join(", ") || "–";
}

export const TALENT_STATUS: Record<string, readonly [string, Tone]> = {
  get active() { return [t("Aktiv", "Active"), "success"] as const; },
  get invited() { return [t("Eingeladen", "Invited"), "info"] as const; },
  get archived() { return [t("Archiviert", "Archived"), "neutral"] as const; },
};
export function talentStatusPill(status: string): Pill {
  const s = TALENT_STATUS[status];
  return s ? pill(s[0], s[1]) : pill(status, "neutral");
}

export const CONTRACT_STATUS: Record<string, readonly [string, Tone]> = {
  get draft() { return [t("Entwurf", "Draft"), "neutral"] as const; },
  get sent() { return [t("Zur Unterschrift", "Awaiting signature"), "info"] as const; },
  get partially_signed() { return [t("Teilweise signiert", "Partially signed"), "warning"] as const; },
  get signed() { return [t("Signiert", "Signed"), "success"] as const; },
  get void() { return [t("Aufgehoben", "Rescinded"), "neutral"] as const; },
};
export function contractPill(c: Pick<ContractRow, "status">): Pill {
  const s = CONTRACT_STATUS[c.status];
  return s ? pill(s[0], s[1]) : pill(c.status, "neutral");
}

export const BOOKING_KIND: Record<string, string> = {
  get commission() { return t("Provision", "Commission"); }, get agency_fee() { return t("Agenturgebühr", "Agency fee"); }, get usage_rights() { return t("Nutzungsrechte", "Usage rights"); }, bonus: "Bonus", get performance() { return t("Leistungsbonus", "Performance bonus"); },
  get expense() { return t("Auslage", "Expense"); }, get correction() { return t("Korrektur", "Correction"); }, get other() { return t("Sonstiges", "Other"); },
};
export function bookingPill(b: Pick<BookingRow, "status">): Pill {
  if (b.status === "booked") return pill(t("Gebucht", "Booked"), "success");
  if (b.status === "cancelled") return pill(t("Storniert", "Cancelled"), "neutral");
  return pill(t("Geplant", "Planned"), "warning");
}

export const INVOICE_KIND: Record<string, string> = {
  get talent_to_agency() { return t("Creator an Agentur", "Creator to agency"); },
  get agency_to_brand() { return ta("Agentur an Marke", "Agency to brand", "An Marke", "To brand"); },
  get talent_to_brand() { return t("Creator an Marke", "Creator to brand"); },
  get agency_to_talent() { return t("Agentur an Creator", "Agency to creator"); },
};

// ── Namen und Eingaben ──────────────────────────────────────────────────────────────────────────

type Named = { display_name: string | null; first_name: string; last_name: string | null };
export function nameOf(t: Named | null | undefined): string {
  if (!t) return "–";
  return t.display_name?.trim() || `${t.first_name} ${t.last_name ?? ""}`.trim();
}

/** „Anna Maria Schmidt“ → Vorname „Anna Maria“, Nachname „Schmidt“; ein Wort bleibt Vorname. */
export function splitName(full: string): { first: string; last: string | null } {
  const parts = full.trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return { first: parts[0] ?? "", last: null };
  return { first: parts.slice(0, -1).join(" "), last: parts[parts.length - 1]! };
}

export function cleanText(v: string | null | undefined): string | null {
  const t = (v ?? "").trim();
  return t === "" ? null : t;
}

/** Social-Handle ohne führendes „@“; leer = null. Leerzeichen im Handle bleiben stehen und fallen in der Prüfung auf. */
export function cleanHandle(v: string | null | undefined): string | null {
  const t = (v ?? "").trim().replace(/^@+/, "").trim();
  return t === "" ? null : t;
}
export const HANDLE_RULE: Record<"instagram" | "tiktok" | "youtube", RegExp> = {
  instagram: /^[A-Za-z0-9._]{1,30}$/,
  tiktok: /^[A-Za-z0-9._]{1,30}$/,
  youtube: /^[A-Za-z0-9._-]{1,100}$/,
};

/** WhatsApp-Nummer ins internationale Format: Leerzeichen und Trenner weg, „00“ wird „+“. */
export function cleanPhone(v: string | null | undefined): string | null {
  let t = (v ?? "").trim().replace(/[\s\-()./]/g, "");
  if (t === "") return null;
  if (t.startsWith("00")) t = `+${t.slice(2)}`;
  return t;
}
export const PHONE_RULE = /^\+[1-9][0-9]{7,14}$/;

export function emailValid(v: string): boolean {
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v.trim());
}

/** „30“ oder „27,5“ → Basispunkte; leer → null; ungültig → undefined. */
export function percentToBp(v: string): number | null | undefined {
  const t = v.trim().replace(",", ".");
  if (t === "") return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0 || n > 100) return undefined;
  return Math.round(n * 100);
}
export function bpToInput(bp: number | null | undefined): string {
  if (bp == null) return "";
  return String(bp / 100).replace(".", ",");
}
export function bpLabel(bp: number): string {
  return `${new Intl.NumberFormat(numberLocale(), { maximumFractionDigits: 2 }).format(bp / 100)} %`;
}

// ── Recht je Land (Beschriftungen aus dem Land des Creators bzw. der Agentur) ────────────────────

/** Kleinunternehmerregelung im Land des Creators (Wortlaut wie die Steuerhinweise der Fachlogik). */
export function smallBusinessLabel(country: string | null | undefined): string {
  switch (normalizeCountry(country ?? "")) {
    case "DE": return t("Kleinunternehmer (§ 19 UStG)", "Small business (§ 19 UStG)");
    case "AT": return t("Kleinunternehmer (§ 6 Abs. 1 Z 27 UStG)", "Small business (§ 6 (1) no. 27 UStG)");
    case "CH": return t("Nicht mehrwertsteuerpflichtig", "Not subject to VAT");
    default: return t("Kleinunternehmer (ohne Umsatzsteuer)", "Small business (without VAT)");
  }
}
/** Gegenstück zur Kleinunternehmerregelung: in der Schweiz und Liechtenstein „MWST-pflichtig“ (SAAS-R2-7), sonst Regelbesteuerung. */
export function regularTaxLabel(country: string | null | undefined): string {
  const c = normalizeCountry(country ?? "");
  return c === "CH" || c === "LI" ? t("MWST-pflichtig", "Subject to VAT") : t("Regelbesteuerung", "Standard taxation");
}
/** Platzhalter der Notiz zur Freistellung von der Abzugsteuer nach dem Land der Agentur (SAAS-R2-8): BZSt bzw. Finanzamt */
export function withholdingNotePlaceholder(orgCountry: string | null | undefined): string {
  return normalizeCountry(orgCountry ?? "") === "AT"
    ? t("z. B. Ansässigkeitsbescheinigung liegt vor (§ 99 EStG, DBA)", "e.g. certificate of residence available (§ 99 EStG, tax treaty)")
    : t("z. B. Bescheinigung vom BZSt liegt vor", "e.g. certificate from the BZSt available");
}
/** Beispiel für die Registerangabe einer Marke je Land. */
export function registerPlaceholder(country: string | null | undefined): string {
  switch (normalizeCountry(country ?? "")) {
    case "AT": return "FN 123456a, Handelsgericht Wien";
    case "CH": return "CHE-123.456.789, Handelsregister Zürich";
    case "DE": return "HRB 12345, Amtsgericht Hamburg";
    default: return t("Registernummer und Registergericht", "Register number and register court");
  }
}
/** Abschnitt „Pauschalversteuerung“ (§ 37b EStG): Einstellung der Agentur, sonst nur bei Agenturen in Deutschland. */
export function flatTaxEnabled(settings: Readonly<Record<string, unknown>>): boolean {
  return typeof settings.flat_tax_enabled === "boolean" ? settings.flat_tax_enabled : settings.country === "DE";
}
/** Alter in ganzen Jahren am Stichtag; ohne Geburtsdatum null. */
export function ageOn(birthDate: string | null | undefined, today: string): number | null {
  if (!birthDate || !/^\d{4}-\d{2}-\d{2}$/.test(birthDate)) return null;
  const [y, m, d] = birthDate.split("-").map(Number) as [number, number, number];
  const [ty, tm, td] = today.split("-").map(Number) as [number, number, number];
  return ty - y - (tm < m || (tm === m && td < d) ? 1 : 0);
}
/** Hinweis zur USt-IdNr. einer Marke: Format des Landes und Ländercode (Reverse Charge braucht eine gültige Nummer). */
export function vatHint(vatId: string | null | undefined, country: string | null | undefined): string | null {
  const v = (vatId ?? "").replace(/[\s.]/g, "").toUpperCase();
  if (!v) return null;
  const c = normalizeCountry(country ?? "");
  const f = checkVatIdFormat(vatId, c);
  if (f?.code === "VAT_ID_LOOKS_LIKE_TAX_NUMBER") return t("Das sieht nach einer Steuernummer aus, nicht nach einer USt-IdNr.", "That looks like a tax number, not a VAT ID.");
  if (f?.code === "VAT_ID_FORMAT") return t(`Format prüfen: erwartet ${String((f.params as { expected?: string } | undefined)?.expected ?? t("Ländercode und Nummer", "country code and number"))}.`, `Check the format: expected ${String((f.params as { expected?: string } | undefined)?.expected ?? t("Ländercode und Nummer", "country code and number"))}.`);
  if (f) return t("Eine USt-IdNr. beginnt mit dem Ländercode.", "A VAT ID starts with the country code.");
  if (c && isEuCountry(c) && c !== "DE" && c !== "AT") {
    const prefix = c === "GR" ? "EL" : c;
    if (!v.startsWith(prefix)) return t(`Die USt-IdNr. einer Marke aus ${countryLabel(c)} beginnt mit ${prefix}.`, `The VAT ID of a brand from ${countryLabel(c)} starts with ${prefix}.`);
  }
  return null;
}

/** Länderauswahl in der Oberflächensprache; DACH zuerst, dann alphabetisch. */
export function countryOptions(): { code: string; name: string }[] {
  const lang = currentLang();
  return COUNTRIES.map((c) => ({ code: c.code, name: c.name[lang] })).sort((a, b) => {
    const top = ["DE", "AT", "CH"];
    const ia = top.indexOf(a.code), ib = top.indexOf(b.code);
    if (ia >= 0 || ib >= 0) return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    return a.name.localeCompare(b.name, lang);
  });
}
export function countryLabel(code: string | null | undefined): string {
  if (!code) return "–";
  return COUNTRIES.find((c) => c.code === code)?.name[currentLang()] ?? code;
}

/** Steuerzone einer Marke als Hinweis für die spätere Rechnung (AD-085, ST-12: unbekannt wird nie geraten). */
export function zoneHint(country: string | null | undefined, orgCountry: string | null | undefined): string {
  if (!country) return t("Steuerzone: unbekannt · bitte ein Land wählen", "Tax zone: unknown · please choose a country");
  const zone = zoneOfCountry(country);
  const own = normalizeCountry(orgCountry ?? "");
  if (zone === "UNKNOWN") return t("Steuerzone: unbekannt", "Tax zone: unknown");
  if (own && normalizeCountry(country) === own) return t(`Steuerzone: Inland (${countryLabel(country)})`, `Tax zone: domestic (${countryLabel(country)})`);
  if (isEuCountry(normalizeCountry(country))) return t(`Steuerzone: EU (${countryLabel(country)})`, `Tax zone: EU (${countryLabel(country)})`);
  return t(`Steuerzone: Drittland (${countryLabel(country)})`, `Tax zone: third country (${countryLabel(country)})`);
}
/** ST-06: USt-IdNr. der Marke ist Pflicht, wenn sie in einem anderen EU-Land sitzt. */
export function vatRequired(country: string | null | undefined, orgCountry: string | null | undefined): boolean {
  const c = normalizeCountry(country ?? "");
  return !!c && isEuCountry(c) && c !== normalizeCountry(orgCountry ?? "");
}

// ── Formulare: Creator ───────────────────────────────────────────────────────────────────────────

export interface TalentForm {
  first_name: string; last_name: string; display_name: string; nickname: string; email: string;
  instagram: string; tiktok: string; youtube: string; whatsapp: string;
  street: string; postal_code: string; city: string; country: string;
  vat_id: string; tax_number: string; small_business: boolean; commission: string;
  language: string; company_name: string; legal_form: string; register_info: string; birth_date: string; guardian_name: string; guardian_email: string;
  guardian2_name: string; guardian2_email: string;
}
export type TalentPatch = {
  first_name: string; last_name: string | null; display_name: string | null; nickname: string | null; email: string | null;
  instagram_handle: string | null; tiktok_handle: string | null; youtube_handle: string | null; whatsapp_phone: string | null;
  street: string | null; postal_code: string | null; city: string | null; country: string | null;
  vat_id: string | null; tax_number: string | null; small_business: boolean; commission_bp: number | null;
  language?: string | null; company_name?: string | null; legal_form?: string | null; register_info?: string | null; birth_date?: string | null;
  guardian_name?: string | null; guardian_email?: string | null; guardian2_name?: string | null; guardian2_email?: string | null;
};
export type FormErrors<F> = Partial<Record<keyof F, string>>;

export function talentFormFrom(t: TalentFull | null | undefined, defaultCountry = "DE"): TalentForm {
  return {
    first_name: t?.first_name ?? "", last_name: t?.last_name ?? "", display_name: t?.display_name ?? "", nickname: t?.nickname ?? "",
    email: t?.email ?? "", instagram: t?.instagram_handle ?? "", tiktok: t?.tiktok_handle ?? "", youtube: t?.youtube_handle ?? "",
    whatsapp: t?.whatsapp_phone ?? "", street: t?.street ?? "", postal_code: t?.postal_code ?? "", city: t?.city ?? "",
    country: t ? (t.country ?? "") : defaultCountry, vat_id: t?.vat_id ?? "", tax_number: t?.tax_number ?? "",
    small_business: t?.small_business ?? false, commission: bpToInput(t?.commission_bp),
    language: t?.language ?? "", company_name: t?.company_name ?? "", legal_form: t?.legal_form ?? "", register_info: t?.register_info ?? "",
    birth_date: t?.birth_date ?? "", guardian_name: t?.guardian_name ?? "", guardian_email: t?.guardian_email ?? "",
    guardian2_name: t?.guardian2_name ?? "", guardian2_email: t?.guardian2_email ?? "",
  };
}

export function validateTalentForm(f: TalentForm): { errors: FormErrors<TalentForm>; patch: TalentPatch | null } {
  const errors: FormErrors<TalentForm> = {};
  const first = f.first_name.trim();
  if (!first) errors.first_name = t("Bitte einen Vornamen angeben.", "Please enter a first name.");
  const email = cleanText(f.email);
  if (email && !emailValid(email)) errors.email = t("Bitte eine gültige E-Mail-Adresse angeben.", "Please enter a valid email address.");
  const ig = cleanHandle(f.instagram), tt = cleanHandle(f.tiktok), yt = cleanHandle(f.youtube);
  if (ig && !HANDLE_RULE.instagram.test(ig)) errors.instagram = t("Nur Buchstaben, Ziffern, Punkt und Unterstrich, höchstens 30 Zeichen.", "Only letters, digits, dot and underscore, at most 30 characters.");
  if (tt && !HANDLE_RULE.tiktok.test(tt)) errors.tiktok = t("Nur Buchstaben, Ziffern, Punkt und Unterstrich, höchstens 30 Zeichen.", "Only letters, digits, dot and underscore, at most 30 characters.");
  if (yt && !HANDLE_RULE.youtube.test(yt)) errors.youtube = t("Nur Buchstaben, Ziffern, Punkt, Binde- und Unterstrich.", "Only letters, digits, dot, hyphen and underscore.");
  const phone = cleanPhone(f.whatsapp);
  if (phone && !PHONE_RULE.test(phone)) errors.whatsapp = t("Bitte im internationalen Format, z. B. +49 170 1234567.", "Please use international format, e.g. +49 170 1234567.");
  const nick = cleanText(f.nickname);
  if (nick && nick.length > 40) errors.nickname = t("Höchstens 40 Zeichen.", "At most 40 characters.");
  const bp = percentToBp(f.commission);
  if (bp === undefined) errors.commission = t("Bitte eine Zahl zwischen 0 und 100 angeben.", "Please enter a number between 0 and 100.");
  const birth = cleanText(f.birth_date);
  if (birth && (!/^\d{4}-\d{2}-\d{2}$/.test(birth) || birth < "1900-01-01")) errors.birth_date = t("Bitte ein Datum wählen.", "Please choose a date.");
  const guardianEmail = cleanText(f.guardian_email);
  if (guardianEmail && !emailValid(guardianEmail)) errors.guardian_email = t("Bitte eine gültige E-Mail-Adresse angeben.", "Please enter a valid email address.");
  const guardian2Email = cleanText(f.guardian2_email ?? "");
  if (guardian2Email && !emailValid(guardian2Email)) errors.guardian2_email = t("Bitte eine gültige E-Mail-Adresse angeben.", "Please enter a valid email address.");
  const language = f.language === "de" || f.language === "en" ? f.language : null;
  if (Object.keys(errors).length) return { errors, patch: null };
  return {
    errors,
    patch: {
      first_name: first, last_name: cleanText(f.last_name), display_name: cleanText(f.display_name), nickname: nick, email: email?.toLowerCase() ?? null,
      instagram_handle: ig, tiktok_handle: tt, youtube_handle: yt, whatsapp_phone: phone,
      street: cleanText(f.street), postal_code: cleanText(f.postal_code), city: cleanText(f.city), country: cleanText(f.country)?.toUpperCase() ?? null,
      vat_id: cleanText(f.vat_id), tax_number: cleanText(f.tax_number), small_business: f.small_business, commission_bp: bp ?? null,
      language, company_name: cleanText(f.company_name), legal_form: cleanText(f.legal_form), register_info: cleanText(f.register_info),
      birth_date: birth, guardian_name: cleanText(f.guardian_name), guardian_email: guardianEmail?.toLowerCase() ?? null,
      guardian2_name: cleanText(f.guardian2_name ?? ""), guardian2_email: guardian2Email?.toLowerCase() ?? null,
    },
  };
}

// ── Formulare: Auszahlungsdaten ──────────────────────────────────────────────────────────────────

export interface PayoutForm {
  bank_account_holder: string; iban: string; bic: string; bank_name: string; bank_country: string;
  payout_method: string; account_number: string; routing_code: string; payout_email: string; payout_currency: string;
}
export const PAYOUT_METHODS = [
  { value: "sepa", get label() { return t("SEPA-Überweisung (IBAN)", "SEPA transfer (IBAN)"); } },
  { value: "swift", get label() { return t("Auslandsüberweisung (SWIFT)", "International transfer (SWIFT)"); } },
  { value: "paypal", label: "PayPal" },
  { value: "wise", label: "Wise" },
] as const;
/** SEPA-Raum: EU, EWR, Schweiz, Vereinigtes Königreich und Kleinstaaten. Innerhalb davon genügt die IBAN (keine BIC nötig). */
const SEPA_EXTRA = ["IS", "LI", "NO", "CH", "GB", "MC", "SM", "AD", "VA"];
export function inSepa(country: string | null | undefined): boolean {
  const c = normalizeCountry(country ?? "");
  return !!c && (isEuCountry(c) || SEPA_EXTRA.includes(c));
}
export function payoutFormFrom(p: PayoutRow | null | undefined): PayoutForm {
  return {
    bank_account_holder: p?.bank_account_holder ?? "", iban: p?.iban ?? "", bic: p?.bic ?? "", bank_name: p?.bank_name ?? "", bank_country: p?.bank_country ?? "",
    payout_method: p?.payout_method ?? "sepa", account_number: p?.account_number ?? "", routing_code: p?.routing_code ?? "", payout_email: p?.payout_email ?? "",
    payout_currency: p?.payout_currency ?? "",
  };
}
/** ST-20: IBAN und BIC formal prüfen. Eine geänderte ungültige Angabe sperrt das Speichern, eine unveränderte bleibt ein Hinweis. */
export function validatePayout(f: PayoutForm, before: PayoutForm, opts: { agenturIbans?: readonly string[] } = {}): { errors: FormErrors<PayoutForm>; hints: string[]; patch: Omit<PayoutRow, "talent_id" | "org_id" | "updated_at"> | null } {
  const errors: FormErrors<PayoutForm> = {};
  const hints: string[] = [];
  const iban = cleanText(f.iban)?.toUpperCase().replace(/\s+/g, "") ?? null;
  const bic = cleanText(f.bic)?.toUpperCase().replace(/\s+/g, "") ?? null;
  const ibanChanged = (cleanText(before.iban)?.toUpperCase().replace(/\s+/g, "") ?? null) !== iban;
  const bicChanged = (cleanText(before.bic)?.toUpperCase().replace(/\s+/g, "") ?? null) !== bic;
  if (iban && !isValidIban(iban)) {
    if (ibanChanged) errors.iban = t("Diese IBAN ist formal ungültig (Prüfziffer).", "This IBAN is formally invalid (check digit).");
    else hints.push(t("Die hinterlegte IBAN ist formal ungültig.", "The IBAN on file is formally invalid."));
  } else if (iban && isQrIban(iban)) {
    // K-534 a: eine QR-IBAN nimmt keine Überweisungen an (die Datenbank lehnt sie beim Ändern ebenso ab, 0370)
    if (ibanChanged) errors.iban = t("Das ist eine QR-IBAN. Sie nimmt nur Zahlungen mit QR-Referenz an, keine Überweisungen. Bitte die normale IBAN eintragen.", "This is a QR-IBAN. It only accepts payments with a QR reference, not transfers. Please enter the normal IBAN.");
    else hints.push(t("Die hinterlegte IBAN ist eine QR-IBAN und nimmt keine Überweisungen an. Bitte die normale IBAN eintragen lassen.", "The IBAN on file is a QR-IBAN and does not accept transfers. Please have the normal IBAN entered."));
  }
  // K-534 b: ein Konto der eigenen Agentur ist als Auszahlungskonto fast immer ein Kopierfehler
  if (iban && (opts.agenturIbans ?? []).some((x) => x.replace(/\s+/g, "").toUpperCase() === iban)) {
    if (ibanChanged) errors.iban = t("Das ist ein Konto deiner Agentur. Bitte die IBAN des Creators eintragen.", "This is one of your agency's accounts. Please enter the creator's IBAN.");
    else hints.push(t("Die hinterlegte IBAN ist ein Konto deiner Agentur. Bitte die IBAN des Creators eintragen.", "The IBAN on file is one of your agency's accounts. Please enter the creator's IBAN."));
  }
  if (bic && !isValidBic(bic)) {
    if (bicChanged) errors.bic = t("Diese BIC entspricht nicht dem SWIFT-Muster.", "This BIC does not match the SWIFT pattern.");
    else hints.push(t("Die hinterlegte BIC entspricht nicht dem SWIFT-Muster.", "The BIC on file does not match the SWIFT pattern."));
  }
  const country = cleanText(f.bank_country)?.toUpperCase() ?? null;
  if (country && !/^[A-Z]{2}$/.test(country)) errors.bank_country = t("Bitte ein Land aus der Liste wählen.", "Please choose a country from the list.");
  const method = PAYOUT_METHODS.some((m) => m.value === f.payout_method) ? f.payout_method : "sepa";
  const payoutEmail = cleanText(f.payout_email)?.toLowerCase() ?? null;
  if ((method === "paypal" || method === "wise") && payoutEmail && !emailValid(payoutEmail)) errors.payout_email = t("Bitte eine gültige E-Mail-Adresse angeben.", "Please enter a valid email address.");
  const account = cleanText(f.account_number)?.replace(/\s+/g, "") ?? null;
  const routing = cleanText(f.routing_code)?.replace(/\s+/g, "") ?? null;
  if (account && !/^[A-Za-z0-9-]{4,34}$/.test(account)) errors.account_number = t("Die Kontonummer hat 4 bis 34 Ziffern oder Buchstaben.", "The account number has 4 to 34 digits or letters.");
  const currency = cleanText(f.payout_currency)?.toUpperCase() ?? null;
  if (Object.keys(errors).length) return { errors, hints, patch: null };
  const groupIban = (s: string | null) => (s ? s.replace(/(.{4})/g, "$1 ").trim() : null);
  const bank = method === "sepa" || method === "swift";
  return {
    errors, hints,
    patch: {
      bank_account_holder: cleanText(f.bank_account_holder), iban: bank ? groupIban(iban) : null, bic: bank ? bic : null, bank_name: bank ? cleanText(f.bank_name) : null,
      bank_country: country, payout_method: method, account_number: method === "swift" ? account : null, routing_code: method === "swift" ? routing : null,
      payout_email: bank ? null : payoutEmail, payout_currency: currency,
    },
  };
}

// ── Formulare: Marke ─────────────────────────────────────────────────────────────────────────────

export interface BrandForm {
  name: string; kind: "brand" | "agency"; legal_name: string; street: string; postal_code: string; city: string; country: string;
  invoice_email: string; vat_id: string; register_info: string; payment_terms_days: string; default_currency: string; language: string;
  is_business: boolean; represented_by: string; invoice_via_agency: boolean; invoice_route: "" | "via_agency" | "direct_to_brand";
  /** 0066: Rechnungen zusätzlich als E-Rechnung (XRechnung) senden; Käuferreferenz bzw. Leitweg-ID */
  e_invoice: boolean; buyer_reference: string;
}
export type BrandPatch = {
  name: string; kind: "brand" | "agency"; legal_name: string | null; street: string | null; postal_code: string | null; city: string | null;
  country: string | null; invoice_email: string | null; vat_id: string | null; register_info: string | null; payment_terms_days: number | null;
  default_currency: string | null; language: string | null; is_business: boolean; represented_by: string | null; invoice_via_agency: boolean;
  invoice_route?: "via_agency" | "direct_to_brand" | null;
  e_invoice?: boolean; buyer_reference?: string | null;
};
/** Käuferreferenz (BT-10) bzw. Leitweg-ID: höchstens so viele Zeichen (wie brands_buyer_reference_check, 0066) */
export const BUYER_REFERENCE_MAX = 100;
/** Sprache einer neuen Marke nach Land (wie die Belege: DACH deutsch, sonst englisch); die Agentur kann sie ändern. */
export function languageForCountry(country: string | null | undefined): "de" | "en" | "" {
  const c = normalizeCountry(country ?? "");
  if (!c) return "";
  return ["DE", "AT", "CH", "LI"].includes(c) ? "de" : "en";
}
export function brandFormFrom(b: BrandFull | null | undefined, defaults: { country?: string | null } = {}): BrandForm {
  return {
    name: b?.name ?? "", kind: b?.kind ?? "brand", legal_name: b?.legal_name ?? "", street: b?.street ?? "", postal_code: b?.postal_code ?? "",
    city: b?.city ?? "", country: b ? (b.country ?? "") : (defaults.country ?? ""), invoice_email: b?.invoice_email ?? "", vat_id: b?.vat_id ?? "",
    register_info: b?.register_info ?? "", payment_terms_days: b?.payment_terms_days == null ? "" : String(b.payment_terms_days),
    default_currency: b?.default_currency ?? "", language: b ? (b.language ?? "") : languageForCountry(defaults.country), is_business: b?.is_business ?? true,
    represented_by: b?.represented_by ?? "", invoice_via_agency: b?.invoice_via_agency ?? false, invoice_route: b?.invoice_route ?? "",
    e_invoice: b?.e_invoice ?? false, buyer_reference: b?.buyer_reference ?? "",
  };
}
export function validateBrandForm(f: BrandForm, orgCountry: string | null): { errors: FormErrors<BrandForm>; hints: string[]; question: string | null; patch: BrandPatch | null } {
  const errors: FormErrors<BrandForm> = {};
  const hints: string[] = [];
  const name = f.name.trim();
  if (!name) errors.name = t("Bitte einen Anzeigenamen angeben.", "Please enter a display name.");
  const email = cleanText(f.invoice_email);
  if (email && !emailValid(email)) errors.invoice_email = t("Bitte eine gültige E-Mail-Adresse angeben.", "Please enter a valid email address.");
  let terms: number | null = null;
  if (f.payment_terms_days.trim() !== "") {
    const n = Number(f.payment_terms_days.trim());
    if (!Number.isInteger(n) || n < 0 || n > 180) errors.payment_terms_days = t("Bitte 0 bis 180 Tage angeben.", "Please enter 0 to 180 days.");
    else terms = n;
  }
  const agency = f.kind === "brand" ? cleanText(f.represented_by) : null;
  const via = f.kind === "brand" && f.invoice_via_agency;
  if (via && !agency) errors.invoice_via_agency = t("Rechnung über die Agentur braucht eine hinterlegte Agentur.", "An invoice via the agency needs an agency on file.");
  if (vatRequired(f.country, orgCountry) && !cleanText(f.vat_id)) hints.push(t("Die USt-IdNr. ist für Rechnungen ins EU-Ausland Pflicht.", "The VAT ID is required for invoices to other EU countries."));
  const vh = vatHint(f.vat_id, f.country);
  if (vh) hints.push(t(`USt-IdNr.: ${vh}`, `VAT ID: ${vh}`));
  const buyerReference = cleanText(f.buyer_reference);
  if (buyerReference && buyerReference.length > BUYER_REFERENCE_MAX) errors.buyer_reference = t(`Bitte höchstens ${BUYER_REFERENCE_MAX} Zeichen angeben.`, `Please enter at most ${BUYER_REFERENCE_MAX} characters.`);
  // E-Rechnung (0066): Pflichtangaben des Empfängers nach XRechnung; ohne sie hält der Versand die Rechnung an
  if (f.e_invoice) {
    const gaps = [!cleanText(f.postal_code) && "PLZ", !cleanText(f.city) && t("Ort", "City"), !cleanText(f.country) && t("Land", "Country")].filter((x): x is string => !!x);
    if (gaps.length) hints.push(t(`Für die E-Rechnung fehlen noch ${gaps.join(", ")} der Marke.`, `For the e-invoice the brand's ${gaps.join(", ")} are still missing.`));
  }
  // AD-084: Kontakt über die Agentur, Rechnung an die Marke → nachfragen, nicht sperren
  const question = agency && !via ? t("Die Marke wird über eine Agentur betreut, die Rechnung geht aber an die Marke selbst. Ist das so gewollt?", "The brand is managed via an agency, but the invoice goes to the brand itself. Is that intended?") : null;
  if (Object.keys(errors).length) return { errors, hints, question, patch: null };
  return {
    errors, hints, question,
    patch: {
      name, kind: f.kind, legal_name: cleanText(f.legal_name), street: cleanText(f.street), postal_code: cleanText(f.postal_code), city: cleanText(f.city),
      country: cleanText(f.country)?.toUpperCase() ?? null, invoice_email: email?.toLowerCase() ?? null, vat_id: cleanText(f.vat_id),
      register_info: cleanText(f.register_info), payment_terms_days: terms, default_currency: cleanText(f.default_currency),
      language: cleanText(f.language), is_business: f.is_business, represented_by: agency, invoice_via_agency: via,
      invoice_route: f.kind === "brand" && f.invoice_route ? f.invoice_route : null,
      e_invoice: f.e_invoice, buyer_reference: buyerReference,
    },
  };
}

/** Zuständigkeiten eines Kontakts (brand_contacts.roles, 0042). Rechnungsempfänger gibt es je Marke genau einen. */
export const CONTACT_ROLES = [
  { key: "invoice", get label() { return t("Rechnung", "Invoice"); } },
  { key: "invoice_cc", get label() { return t("Rechnung in Kopie", "Invoice in copy"); } },
  { key: "contract", get label() { return t("Vertrag", "Contract"); } },
  { key: "briefing", label: "Briefing" },
  // 0073: Empfänger des Freigabe-Links für Entwürfe
  { key: "approval", get label() { return t("Freigabe", "Approval"); } },
  { key: "insights", label: "Insights" },
] as const;
export function contactRoles(c: Pick<ContactRow, "roles" | "is_invoice_recipient">): string[] {
  const r = [...(c.roles ?? [])];
  if (c.is_invoice_recipient && !r.includes("invoice")) r.push("invoice");
  return CONTACT_ROLES.map((x) => x.key as string).filter((k) => r.includes(k));
}
export interface ContactForm { name: string; email: string; phone: string; role: string; is_invoice_recipient: boolean; roles: string[] }
export function contactFormFrom(c: ContactRow | null | undefined): ContactForm {
  return {
    name: c?.name ?? "", email: c?.email ?? "", phone: c?.phone ?? "", role: c?.role ?? "", is_invoice_recipient: c?.is_invoice_recipient ?? false,
    roles: c ? contactRoles(c) : [],
  };
}
export function validateContact(f: ContactForm): { errors: FormErrors<ContactForm>; patch: Omit<ContactRow, "id" | "org_id" | "brand_id" | "created_at"> | null } {
  const errors: FormErrors<ContactForm> = {};
  if (!f.name.trim()) errors.name = t("Bitte einen Namen angeben.", "Please enter a name.");
  const email = cleanText(f.email);
  if (email && !emailValid(email)) errors.email = t("Bitte eine gültige E-Mail-Adresse angeben.", "Please enter a valid email address.");
  const roles = CONTACT_ROLES.map((x) => x.key as string).filter((k) => f.roles.includes(k) || (k === "invoice" && f.is_invoice_recipient));
  if (roles.some((r) => r !== "briefing") && !email) errors.email = t("Für Rechnung, Vertrag, Freigabe und Insights braucht der Kontakt eine E-Mail-Adresse.", "For invoice, contract, approval and insights the contact needs an email address.");
  if (Object.keys(errors).length) return { errors, patch: null };
  return {
    errors,
    patch: { name: f.name.trim(), email: email?.toLowerCase() ?? null, phone: cleanText(f.phone), role: cleanText(f.role), is_invoice_recipient: roles.includes("invoice"), roles },
  };
}
/**
 * Empfänger je Zweck (eine Regel für Stammdaten und Insights): für Rechnungen die Rechnungsmail der Marke, sonst der Kontakt
 * mit der Rolle „Rechnung“; für Vertrag, Briefing und Insights der Kontakt mit dieser Rolle, sonst der erste Kontakt mit Mail.
 */
/**
 * Insights-Freigabe (Entscheidung 16.10.2026): jede Adresse ist erlaubt, aber eine Adresse, die bei der Marke weder als Kontakt
 * noch als Rechnungsadresse gespeichert ist, bekommt einen Hinweis (Tippfehler, fremde Person). Ohne gültige Adresse kein Hinweis.
 */
export function shareRecipientWarning(email: string, brand: Pick<BrandFull, "id" | "invoice_email" | "name"> | null | undefined, contacts: readonly ContactRow[]): string | null {
  const e = email.trim().toLowerCase();
  if (!emailValid(e)) return null;
  if (!brand) return t("Keine Marke gewählt: Prüf die Adresse genau, nur mit ihr lässt sich die Freigabe öffnen.", "No brand selected: check the address carefully, the share can only be opened with it.");
  const known = [brand.invoice_email, ...contacts.filter((c) => c.brand_id === brand.id).map((c) => c.email)].some((x) => x?.trim().toLowerCase() === e);
  return known ? null : t(`Diese Adresse ist bei ${brand.name} nicht als Kontakt gespeichert. Prüf sie genau, bevor du die Freigabe erstellst.`,
    `This address is not saved as a contact of ${brand.name}. Check it carefully before you create the share.`);
}

export function contactFor(role: "invoice" | "contract" | "briefing" | "insights", brand: Pick<BrandFull, "id" | "invoice_email">, contacts: readonly ContactRow[]): { name: string | null; email: string } | null {
  const own = contacts.filter((c) => c.brand_id === brand.id && c.email);
  if (role === "invoice" && brand.invoice_email) return { name: null, email: brand.invoice_email };
  const withRole = own.find((c) => contactRoles(c).includes(role));
  if (withRole) return { name: withRole.name, email: withRole.email! };
  if (role === "invoice") return null;
  const any = own.find((c) => !contactRoles(c).includes("invoice")) ?? own[0];
  return any ? { name: any.name, email: any.email! } : null;
}

// ── Umsatz: dieselbe Rechnung wie die Übersicht ──────────────────────────────────────────────────

export function feeOf(mode: string | null | undefined, bp: number | null | undefined, cents: number | null | undefined): OverviewFee {
  if (mode === "percent" && bp != null) return { mode: "percent", percent: bp / 100 };
  if (mode === "fixed" && cents != null) return { mode: "fixed", cents };
  return { mode: "none" };
}

export interface RevenueSource {
  today: string;
  currency: string;
  settings: Pick<OrgSettingsRow, "default_commission_bp" | "agency_fee_mode" | "agency_fee_bp" | "agency_fee_cents"> & { invoice_route?: string | null; revenue_start?: unknown };
  talents: readonly Pick<TalentRow, "id" | "commission_bp">[];
  deals: readonly DealFull[];
  periods: readonly PeriodRow[];
  extras: readonly LineItemRow[];
  terms: readonly AgencyTermsRow[];
  invoices: readonly InvoiceRow[];
  bookings: readonly BookingRow[];
  /** Wechselkurse (fx_rates_monthly): Kooperationen in anderer Währung rechnen wie auf der Übersicht um (SAAS-R2-1) */
  fx?: readonly FxRateRow[];
  /** Marken (Abrechnungsweg) und Verträge (Umsatzbeginn „Vertrag unterschrieben“) wie auf der Übersicht */
  brands?: readonly { id: string; invoice_route?: string | null }[];
  contracts?: readonly { deal_id: string | null; status: string }[];
}

/**
 * Alle Umsatz-Vorkommen (Kooperationsmonate und Buchungen) wie auf der Übersicht: dieselbe Umwandlung (overviewDealFromRow,
 * overviewPeriodFromRow) und dieselbe Umrechnung in die Agenturwährung (Kurse aus fx_rates, Kreuzkurse über den Euro, eingefrorene
 * Beträge von Buchungen). Nur Beträge, für die wirklich kein Kurs vorliegt, bleiben außen vor und werden gezählt (SAAS-R2-1).
 */
export function occurrencesOf(src: RevenueSource): { occ: Occurrence[]; unconverted: number } {
  const terms = new Map(src.terms.map((t) => [t.deal_id, t]));
  const month = src.today.slice(0, 7);
  const brandRoute = new Map((src.brands ?? []).map((b) => [b.id, b.invoice_route ?? null]));
  const signed = new Set((src.contracts ?? []).filter((c) => c.deal_id && c.status === "signed").map((c) => c.deal_id!));
  const input: OverviewInput = {
    today: src.today, from: month, to: month, talentId: null, currency: src.currency, orgCurrency: src.currency,
    agencyCommissionPercent: (src.settings.default_commission_bp ?? 0) / 100,
    talentCommissionPercent: Object.fromEntries(src.talents.map((t) => [t.id, t.commission_bp == null ? null : t.commission_bp / 100])),
    agencyFee: feeOf(src.settings.agency_fee_mode, src.settings.agency_fee_bp, src.settings.agency_fee_cents),
    deals: src.deals.map((d) => {
      const t = terms.get(d.id);
      return overviewDealFromRow(d as DealFull & DealRowForOverview, { fee: t ? feeOf(t.agency_fee_mode, t.agency_fee_bp, t.agency_fee_cents) : null,
        terms: t as Parameters<typeof overviewDealFromRow>[1]["terms"], brandRoute: brandRoute.get(d.brand_id) ?? null,
        orgRoute: src.settings.invoice_route ?? null, revenueStart: (src.settings.revenue_start as string | null | undefined) ?? null, signed: signed.has(d.id) });
    }),
    periods: src.periods.map((p) => overviewPeriodFromRow(p as PeriodRow & PeriodRowForOverview)),
    extras: src.extras.map((e) => ({ dealId: e.deal_id, periodId: e.period_id, amountCents: e.amount_cents, commissionable: e.commissionable,
      createdAt: (e as LineItemRow & { created_at?: string | null }).created_at ?? null })),
    invoices: src.invoices.map((i) => overviewInvoiceFromRow({ ...i, amount_org_cents: (i.amount_org_cents as number | null | undefined) ?? null, bad_debt_at: (i.bad_debt_at as string | null | undefined) ?? null })),
    bookings: src.bookings.map((b) => ({
      id: b.id, dealId: b.deal_id, periodId: (b as BookingRow & { period_id?: string | null }).period_id ?? null, talentId: b.talent_id, brandId: b.brand_id, kind: b.kind,
      bookedOn: b.booked_on, amountCents: b.amount_cents, currency: b.currency, agencyShareCents: b.agency_share_cents, status: b.status,
      fxRateToOrg: (b as BookingRow & { fx_rate_to_org?: number | null }).fx_rate_to_org ?? null, amountOrgCents: (b as BookingRow & { amount_org_cents?: number | null }).amount_org_cents ?? null,
    })),
    convert: fxMonthConverter(src.fx ?? []),
  };
  const all = dealOccurrences(input);
  const occ = all.filter((o) => !Number.isNaN(o.managementCents));
  return { occ, unconverted: all.length - occ.length };
}

/** Hinweis auf Monate ohne Kurs (SAAS-R2-1): nur wenn wirklich kein Kurs vorliegt, mit richtiger Einzahl. */
export function ohneKursText(n: number): string {
  if (n <= 0) return "";
  return n === 1
    ? t("1 Monat in anderer Währung ohne hinterlegten Kurs ist in den Umsätzen nicht enthalten.", "1 month in another currency without an exchange rate is not included in the revenue.")
    : t(`${n} Monate in anderer Währung ohne hinterlegten Kurs sind in den Umsätzen nicht enthalten.`, `${n} months in another currency without an exchange rate are not included in the revenue.`);
}

export interface RevenueTotals {
  /** Kooperationsvolumen netto: Budget + Zusatzpositionen + Agenturgebühr */
  gross: number;
  /** Agenturanteil netto: Provision + Gebühr */
  agency: number;
  fee: number;
  /** Creator-Anteil netto: Volumen − Agenturanteil (nie negativ) */
  creator: number;
  bookedGross: number;
  bookedAgency: number;
  bookedCreator: number;
  /** Kooperationen mit Umsatz (manuelle Buchung ohne Kooperation zählt einzeln, KZ-12) */
  deals: number;
}
const creatorOf = (o: Occurrence) => Math.max(0, o.grossCents - o.managementCents);
export function totalsOf(list: readonly Occurrence[]): RevenueTotals {
  const t: RevenueTotals = { gross: 0, agency: 0, fee: 0, creator: 0, bookedGross: 0, bookedAgency: 0, bookedCreator: 0, deals: 0 };
  const deals = new Set<string>();
  for (const o of list) {
    t.gross += o.grossCents;
    t.agency += o.managementCents;
    t.fee += o.feeCents;
    t.creator += creatorOf(o);
    if (o.booked) {
      t.bookedGross += o.grossCents;
      t.bookedAgency += o.managementCents;
      t.bookedCreator += creatorOf(o);
    }
    deals.add(o.dealId);
  }
  t.deals = deals.size;
  return t;
}
export const upToNow = (list: readonly Occurrence[], today: string) => list.filter((o) => o.month <= today.slice(0, 7));
export const inYear = (list: readonly Occurrence[], today: string) => list.filter((o) => o.month.startsWith(today.slice(0, 4)));
export const planned = (list: readonly Occurrence[], today: string) => list.filter((o) => o.month > today.slice(0, 7));

export interface MonthPoint { month: string; gross: number; agency: number; creator: number }
/** Umsatzverlauf der letzten n Monate bis einschließlich des laufenden Monats (AD-031). */
export function monthlySeries(list: readonly Occurrence[], today: string, n = 12): MonthPoint[] {
  const to = today.slice(0, 7);
  return monthRange(addMonths(to, -(n - 1)), to).map((m) => {
    const t = totalsOf(list.filter((o) => o.month === m));
    return { month: m, gross: t.gross, agency: t.agency, creator: t.creator };
  });
}

export interface Rank { id: string; agency: number; gross: number }
export function rankBy(list: readonly Occurrence[], key: "brandId" | "talentId"): Rank[] {
  const m = new Map<string, Rank>();
  for (const o of list) {
    const id = o[key];
    if (!id) continue;
    const e = m.get(id) ?? { id, agency: 0, gross: 0 };
    e.agency += o.managementCents;
    e.gross += o.grossCents;
    m.set(id, e);
  }
  return [...m.values()].sort((a, b) => b.agency - a.agency || b.gross - a.gross);
}

// ── Kooperationen ────────────────────────────────────────────────────────────────────────────────

/** Status-Katalog für die Aktiv-Regel KA-12 (Endstatus abgeschlossen, storniert, blockiert). */
export const DEAL_CATALOG: DealCatalog = {
  statuses: [
    ...["planning", "contract", "concept", "production", "content_approval", "published", "reporting", "invoicing"].map((key) => ({ key, countsAsRevenue: key !== "planning" })),
    { key: "done", countsAsRevenue: true, terminal: "completed" },
    { key: "blocked", countsAsRevenue: false, terminal: "blocked" },
    { key: "cancelled", countsAsRevenue: false, terminal: "cancelled" },
  ],
  models: [
    { key: "fixed", revenue: true, monthBased: false }, { key: "monthly", revenue: true, monthBased: true },
    { key: "installments", revenue: true, monthBased: true }, { key: "commission_only", revenue: true, monthBased: true },
    { key: "fixed_plus_commission", revenue: true, monthBased: true }, { key: "custom", revenue: true, monthBased: true },
    { key: "barter", revenue: false, monthBased: false }, { key: "event", revenue: false, monthBased: false }, { key: "gifting", revenue: false, monthBased: false },
  ],
};

export interface DealLine {
  deal: DealFull;
  talentName: string;
  brandName: string;
  pill: Pill;
  active: boolean;
  /** Monate, die noch nicht erledigt sind (KA-11/KA-12) */
  openMonths: number;
  months: string[];
  /** Honorar netto: Monatsbeträge bzw. Budget plus Zusatzpositionen */
  totalCents: number;
  nextDeadline: string | null;
  /** Monat für den Sprung in die Kooperation (?monat=) */
  focusMonth: string | null;
}
export function dealLines(input: {
  deals: readonly DealFull[]; periods: readonly PeriodRow[]; extras: readonly LineItemRow[]; views: readonly InvoiceView[];
  talentName: (id: string) => string; brandName: (id: string) => string; today: string;
}): DealLine[] {
  const periodsByDeal = new Map<string, PeriodRow[]>();
  for (const p of input.periods) periodsByDeal.set(p.deal_id, [...(periodsByDeal.get(p.deal_id) ?? []), p]);
  const extrasByDeal = new Map<string, number>();
  for (const e of input.extras) extrasByDeal.set(e.deal_id, (extrasByDeal.get(e.deal_id) ?? 0) + e.amount_cents);
  const recordsByPeriod = new Map<string, InvoiceView["record"][]>();
  for (const v of input.views) if (v.row.period_id) recordsByPeriod.set(v.row.period_id, [...(recordsByPeriod.get(v.row.period_id) ?? []), v.record]);
  const current = input.today.slice(0, 7);

  return input.deals.map((d) => {
    const ps = [...(periodsByDeal.get(d.id) ?? [])].sort((a, b) => a.month.localeCompare(b.month));
    const months = ps.map((p) => p.month.slice(0, 7));
    const dealMonths: DealMonth[] = ps.map((p) => ({
      month: p.month.slice(0, 7),
      status: p.status === "paid" ? "paid" : (p.manual_status ?? d.status),
      invoices: recordsByPeriod.get(p.id) ?? [],
      invoiceRequestedAt: p.invoice_requested_at,
    }));
    const act = d.archived_at ? { active: false, openMonths: 0 } : isDealActive(DEAL_CATALOG, d.status, dealMonths);
    const base = ps.length ? ps.reduce((a, p) => a + (p.amount_cents ?? 0), 0) : (d.budget_cents ?? 0);
    const deadlines = [d.content_deadline, ...ps.map((p) => p.content_deadline)].filter((x): x is string => !!x && x >= input.today).sort();
    const nextDeadline = deadlines[0] ?? null;
    const focus = nextDeadline
      ? (ps.find((p) => p.content_deadline === nextDeadline)?.month.slice(0, 7) ?? months.find((m) => m >= current) ?? null)
      : (months.find((m) => m === current) ?? months[months.length - 1] ?? null);
    return {
      deal: d, talentName: input.talentName(d.talent_id), brandName: input.brandName(d.brand_id), pill: dealStatusPill(d),
      active: act.active, openMonths: act.openMonths, months, totalCents: base + (extrasByDeal.get(d.id) ?? 0), nextDeadline, focusMonth: focus,
    };
  }).sort((a, b) => Number(b.active) - Number(a.active) || (b.months[b.months.length - 1] ?? b.deal.start_month ?? "").localeCompare(a.months[a.months.length - 1] ?? a.deal.start_month ?? ""));
}
export function termLabel(line: Pick<DealLine, "months" | "deal">): string {
  const first = line.months[0] ?? line.deal.start_month?.slice(0, 7) ?? null;
  const last = line.months[line.months.length - 1] ?? first;
  if (!first) return "–";
  return first === last ? monthLabel(first) : `${monthLabel(first)} – ${monthLabel(last!)}`;
}
export type DealFilter = "alle" | "aktiv" | "abgeschlossen";
export function matchesDealFilter(l: DealLine, f: DealFilter): boolean {
  if (f === "aktiv") return l.active;
  if (f === "abgeschlossen") return !l.active;
  return true;
}

// ── Rechnungen, offene Summe, Verzug ─────────────────────────────────────────────────────────────

export const TO_BRAND = new Set(["agency_to_brand", "talent_to_brand"]);
const byIssueDesc = (a: InvoiceView, b: InvoiceView) =>
  (b.row.issue_date ?? b.row.created_at).localeCompare(a.row.issue_date ?? a.row.created_at);

export function makeViews(invoices: readonly InvoiceRow[], ctx: { cfg: OrgConfig; today: string; holidayRegion: string | null; periods: readonly PeriodRow[] }): InvoiceView[] {
  const periods = new Map(ctx.periods.map((p) => [p.id, p]));
  const v = { cfg: ctx.cfg, today: ctx.today, holidayRegion: ctx.holidayRegion, periods };
  return invoices.map((r) => viewInvoice(r, v)).filter((x): x is InvoiceView => !!x);
}
export function invoicesOfTalent(views: readonly InvoiceView[], talentId: string): InvoiceView[] {
  return views.filter((v) => v.row.talent_id === talentId).sort(byIssueDesc);
}
export function invoicesOfBrand(views: readonly InvoiceView[], deals: ReadonlyMap<string, Pick<DealRow, "brand_id">>, brandId: string): InvoiceView[] {
  return views
    .filter((v) => TO_BRAND.has(v.row.kind) && (v.row.brand_id === brandId || (!v.row.brand_id && !!v.row.deal_id && deals.get(v.row.deal_id)?.brand_id === brandId)))
    .sort(byIssueDesc);
}

export interface Sum { currency: string; cents: number }
function addSum(list: Sum[], currency: string, cents: number) {
  const e = list.find((x) => x.currency === currency);
  if (e) e.cents += cents;
  else list.push({ currency, cents });
}
export interface OpenInfo { count: number; sums: Sum[] }
/** Offener Bruttobetrag der Rechnungen an Marken (Teilzahlungen gibt es nicht). */
export function openInfo(views: readonly InvoiceView[]): OpenInfo {
  const sums: Sum[] = [];
  let count = 0;
  for (const v of views) {
    if (!TO_BRAND.has(v.row.kind)) continue;
    const b = balanceCents(v);
    if (b <= 0 || ["draft", "in_review", "correction_needed"].includes(v.state.state)) continue;
    count += 1;
    addSum(sums, v.row.currency, b);
  }
  return { count, sums };
}
/** Offene Auszahlungen an den Creator: freigegebene Creator-Rechnungen, noch nicht ausgezahlt. */
export function payoutInfo(views: readonly InvoiceView[]): OpenInfo & { inReview: number } {
  const sums: Sum[] = [];
  let count = 0, inReview = 0;
  for (const v of views) {
    if (v.row.kind !== "talent_to_agency") continue;
    if (v.state.state === "in_review") { inReview += 1; continue; }
    const b = balanceCents(v);
    if (b <= 0 || ["draft", "correction_needed"].includes(v.state.state)) continue;
    count += 1;
    addSum(sums, v.row.currency, b);
  }
  return { count, sums, inReview };
}
export interface OverdueInfo { count: number; sums: Sum[]; oldestDays: number; oldestNumber: string | null; oldestId: string | null }
/** Verzug (AD-030/AD-031): überfällige Rechnungen an Marken, Summe und älteste. */
export function overdueInfo(views: readonly InvoiceView[]): OverdueInfo | null {
  const list = views.filter((v) => TO_BRAND.has(v.row.kind) && v.state.isOverdue && balanceCents(v) > 0);
  if (!list.length) return null;
  const sums: Sum[] = [];
  for (const v of list) addSum(sums, v.row.currency, balanceCents(v));
  const oldest = [...list].sort((a, b) => b.state.daysOverdue - a.state.daysOverdue)[0]!;
  return { count: list.length, sums, oldestDays: oldest.state.daysOverdue, oldestNumber: oldest.row.number, oldestId: oldest.row.id };
}
export function sumsLabel(sums: readonly Sum[], fmt: (cents: number, currency: string) => string): string {
  return sums.length ? sums.map((s) => fmt(s.cents, s.currency)).join(" + ") : fmt(0, "EUR");
}

// ── Zugang und Einladungen ───────────────────────────────────────────────────────────────────────

export type InvitationState = "open" | "expired" | "accepted" | "revoked";
export function invitationState(inv: Pick<InvitationRow, "accepted_at" | "revoked_at" | "expires_at">, nowMs: number): InvitationState {
  if (inv.accepted_at) return "accepted";
  if (inv.revoked_at) return "revoked";
  return Date.parse(inv.expires_at) <= nowMs ? "expired" : "open";
}
export function invitationPill(s: InvitationState): Pill {
  switch (s) {
    case "open": return pill(t("Offen", "Open"), "info");
    case "expired": return pill(t("Abgelaufen", "Expired"), "warning");
    case "accepted": return pill(t("Angenommen", "Accepted"), "success");
    case "revoked": return pill(t("Zurückgezogen", "Withdrawn"), "neutral");
  }
}

export type AccessState = "linked" | "invited" | "expired" | "revoked" | "none";
export interface Access { state: AccessState; pill: Pill; invitation: InvitationRow | null; link: LinkRow | null }
/** Konto-/Einladungsstatus eines Creators (ersetzt Nutzername und Passwort des Altportals, AD-077). */
export function accessOf(talentId: string, links: readonly LinkRow[], invitations: readonly InvitationRow[], nowMs: number): Access {
  const link = links.find((l) => l.talent_id === talentId) ?? null;
  const invitation = invitations
    .filter((i) => i.kind === "talent" && i.talent_id === talentId)
    .sort((a, b) => b.created_at.localeCompare(a.created_at))[0] ?? null;
  if (link) return { state: "linked", pill: pill(t("Zugang aktiv", "Access active"), "success"), invitation, link };
  if (!invitation) return { state: "none", pill: pill(t("Kein Zugang", "No access"), "neutral"), invitation, link };
  const s = invitationState(invitation, nowMs);
  if (s === "open") return { state: "invited", pill: pill(t("Eingeladen", "Invited"), "info"), invitation, link };
  if (s === "expired") return { state: "expired", pill: pill(t("Einladung abgelaufen", "Invitation expired"), "warning"), invitation, link };
  if (s === "revoked") return { state: "revoked", pill: pill(t("Einladung zurückgezogen", "Invitation withdrawn"), "neutral"), invitation, link };
  return { state: "none", pill: pill(t("Kein Zugang", "No access"), "neutral"), invitation, link };
}
/** Weg der App zur Einladung (creatorPublicRoutes: /einladung/:token). */
export function invitationLink(origin: string, token: string): string {
  return `${origin.replace(/\/$/, "")}/einladung/${token}`;
}
export function shareLink(origin: string, token: string): string {
  return `${origin.replace(/\/$/, "")}/i/${token}`;
}

// ── Nachfass-Hinweis (AD-030) ────────────────────────────────────────────────────────────────────

export function followUp(last: string | null, next: string | null, today: string): { label: string; tone: Tone; days: number | null; kurz: string; detail: string | null } {
  // label: der ganze Satz; kurz und detail: dasselbe als kurze Statusmarke plus Nebenzeile (Kennzahl-Kachel, Pille bleibt einzeilig)
  const current = today.slice(0, 7);
  const geplant = next && next > current ? monthLabel(next) : null;
  const nextNote = geplant ? t(` · nächste geplant: ${geplant}`, ` · next planned: ${geplant}`) : "";
  const tage = (d: number) => `${d} ${d === 1 ? t("Tag", "day") : t("Tagen", "days")}`;
  if (!last) {
    return next
      ? { label: t(`Geplant ab ${monthLabel(next)}`, `Planned from ${monthLabel(next)}`), tone: "info", days: null, kurz: t("Geplant", "Planned"), detail: t(`ab ${monthLabel(next)}`, `from ${monthLabel(next)}`) }
      : { label: t("Noch keine Kooperation", "No deal yet"), tone: "neutral", days: null, kurz: t("Keine Kooperation", "No deal yet"), detail: null };
  }
  if (last >= current) return { label: t(`Laufende Kooperation${nextNote}`, `Ongoing deal${nextNote}`), tone: "success", days: 0, kurz: t("Laufend", "Ongoing"), detail: geplant ? t(`nächste geplant: ${geplant}`, `next planned: ${geplant}`) : null };
  const days = Math.max(0, diffDays(lastDayOfMonth(last), today));
  if (geplant) return { label: t(`Nächste Kooperation geplant: ${geplant} · letzte vor ${tage(days)}`, `Next deal planned: ${geplant} · last one ${tage(days)} ago`), tone: "success", days, kurz: t("Nächste geplant", "Next planned"), detail: t(`${geplant} · letzte vor ${tage(days)}`, `${geplant} · last one ${tage(days)} ago`) };
  if (days > 120) return { label: t(`Nachfassen empfohlen · seit ${days} Tagen`, `Follow-up recommended · for ${days} days`), tone: "warning", days, kurz: t("Nachfassen", "Follow up"), detail: t(`seit ${days} Tagen`, `for ${days} days`) };
  if (days > 60) return { label: t(`Erinnerung sinnvoll · seit ${days} Tagen`, `Reminder advisable · for ${days} days`), tone: "info", days, kurz: t("Erinnern", "Remind"), detail: t(`seit ${days} Tagen`, `for ${days} days`) };
  return { label: t(`vor ${tage(days)}${nextNote}`, `${tage(days)} ago${nextNote}`), tone: "neutral", days, kurz: t(`vor ${tage(days)}`, `${tage(days)} ago`), detail: geplant ? t(`nächste geplant: ${geplant}`, `next planned: ${geplant}`) : null };
}
/** Letzter Kooperationsmonat bis heute und nächster geplanter Monat (ohne stornierte und archivierte Kooperationen). */
export function lastAndNext(deals: readonly DealFull[], periods: readonly PeriodRow[], today: string): { last: string | null; next: string | null } {
  const ids = new Set(deals.filter((d) => d.status !== "cancelled" && !d.archived_at).map((d) => d.id));
  const months = periods.filter((p) => ids.has(p.deal_id)).map((p) => p.month.slice(0, 7));
  for (const d of deals) if (ids.has(d.id) && d.start_month && !periods.some((p) => p.deal_id === d.id)) months.push(d.start_month.slice(0, 7));
  const current = today.slice(0, 7);
  const past = months.filter((m) => m <= current).sort();
  const future = months.filter((m) => m > current).sort();
  return { last: past[past.length - 1] ?? null, next: future[0] ?? null };
}

// ── Pauschalversteuerung (AD-031, V130) ──────────────────────────────────────────────────────────

export interface FlatTaxLine { dealId: string; title: string; month: string | null; monthLabel: string; company: string; flat: boolean }
export function flatTaxLines(deals: readonly DealFull[], periods: readonly PeriodRow[], brandName: (id: string) => string): FlatTaxLine[] {
  const out: FlatTaxLine[] = [];
  for (const d of deals) {
    const months = [...new Set(periods.filter((p) => p.deal_id === d.id).map((p) => p.month.slice(0, 7)))].sort();
    const keys = months.length ? months : [d.start_month ? d.start_month.slice(0, 7) : null];
    for (const m of keys) out.push({ dealId: d.id, title: d.title, month: m, monthLabel: m ? monthLabel(m) : "–", company: brandName(d.brand_id), flat: d.products_flat_taxed });
  }
  return out.sort((a, b) => a.title.localeCompare(b.title, "de") || (a.month ?? "").localeCompare(b.month ?? ""));
}
/** CSV wie im Altportal: Semikolon, UTF-8 mit BOM, Schutz gegen Formeln in Zellen. */
export function flatTaxCsv(lines: readonly FlatTaxLine[]): string {
  const q = (v: string) => {
    const s = csvCellGuard(v);
    return /[";\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const rows = [[t("Kooperation", "Deal"), t("Monat", "Month"), t("Firma", "Company"), t("Pauschal versteuert", "Flat-rate taxed")], ...lines.map((l) => [l.title, l.monthLabel, l.company, l.flat ? t("Ja", "Yes") : t("Nein", "No")])];
  return `﻿${rows.map((r) => r.map(q).join(";")).join("\r\n")}`;
}
export function fileSlug(name: string): string {
  return name.normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase() || "creator";
}

// ── Instagram-Insights ───────────────────────────────────────────────────────────────────────────

/** Blöcke wie app.ig_blocks() (0023), Beschreibung nach dem Blockkatalog des Altportals. */
export const IG_BLOCKS: readonly { key: string; label: string; group: "zielgruppe" | "leistung"; hint: string }[] = [
  { key: "profile", get label() { return t("Profilkopf", "Profile header"); }, group: "zielgruppe", get hint() { return t("Name, @handle, Follower, Anzahl Beiträge.", "Name, @handle, followers, number of posts."); } },
  { key: "age", get label() { return t("Altersgruppen", "Age groups"); }, group: "zielgruppe", get hint() { return t("Altersverteilung der Zielgruppe.", "Age distribution of the audience."); } },
  { key: "gender", get label() { return t("Geschlecht", "Gender"); }, group: "zielgruppe", get hint() { return t("Geschlechterverteilung der Follower.", "Gender distribution of the followers."); } },
  { key: "countries", get label() { return t("Länder", "Countries"); }, group: "zielgruppe", get hint() { return t("Die stärksten Länder der Zielgruppe.", "The audience's top countries."); } },
  { key: "cities", get label() { return t("Städte", "Cities"); }, group: "zielgruppe", get hint() { return t("Die stärksten Städte der Zielgruppe.", "The audience's top cities."); } },
  { key: "reach", get label() { return t("Reichweite", "Reach"); }, group: "leistung", get hint() { return t("Erreichte Konten im Zeitraum, nicht die Aufrufe.", "Accounts reached in the period, not the views."); } },
  { key: "views", get label() { return t("Aufrufe", "Views"); }, group: "leistung", get hint() { return t("Aufrufe im Zeitraum, getrennt von der Reichweite.", "Views in the period, separate from reach."); } },
  { key: "interactions", get label() { return t("Interaktionen", "Interactions"); }, group: "leistung", get hint() { return t("Likes, Kommentare, Gespeichert, Geteilt.", "Likes, comments, saves, shares."); } },
  { key: "stories", get label() { return t("Story-Aufrufe", "Story views"); }, group: "leistung", get hint() { return t("Story-Aufrufe, Story-Reichweite und Antworten.", "Story views, story reach and replies."); } },
  { key: "content_types", get label() { return t("Reels und Beiträge im Vergleich", "Reels and posts compared"); }, group: "leistung", get hint() { return t("Kennzahlen je Format.", "Metrics per format."); } },
  { key: "follower_split", get label() { return t("Follower und Nicht-Follower", "Followers and non-followers"); }, group: "leistung", get hint() { return t("Reichweite und Aufrufe nach Follower-Status.", "Reach and views by follower status."); } },
  { key: "profile_clicks", get label() { return t("Klicks im Profil", "Profile clicks"); }, group: "leistung", get hint() { return t("Tipps auf die Schaltflächen im Profil.", "Taps on the buttons in the profile."); } },
  { key: "top_posts", get label() { return t("Top-Beiträge", "Top posts"); }, group: "leistung", get hint() { return t("Die stärksten Beiträge im Zeitraum.", "The strongest posts in the period."); } },
  { key: "story_items", get label() { return t("Storys einzeln", "Individual stories"); }, group: "leistung", get hint() { return t("Reichweite, Aufrufe und Navigation je Story, gemessen solange die Story lebt. Klicks auf Link-Sticker liefert Instagram nicht.", "Reach, views and navigation per story, measured while the story is live. Instagram does not provide link sticker taps."); } },
];
export const IG_PERIODS: readonly { key: string; label: string }[] = [
  { key: "7d", get label() { return t("Letzte 7 Tage", "Last 7 days"); } },
  { key: "30d", get label() { return t("Letzte 30 Tage", "Last 30 days"); } },
  { key: "90d", get label() { return t("Letzte 90 Tage", "Last 90 days"); } },
  { key: "12m", get label() { return t("Letzte 12 Monate (Monatswerte)", "Last 12 months (monthly values)"); } },
  { key: "all", get label() { return t("Gesamter Verlauf (Monatswerte)", "Whole history (monthly values)"); } },
];
export function blockLabel(key: string): string {
  return IG_BLOCKS.find((b) => b.key === key)?.label ?? key;
}
export function periodLabel(key: string): string {
  return IG_PERIODS.find((p) => p.key === key)?.label ?? key;
}
const METRIC: Record<string, string> = {
  get reach() { return t("Reichweite", "Reach"); }, get views() { return t("Aufrufe", "Views"); }, get interactions() { return t("Interaktionen", "Interactions"); }, followers: "Follower", get followers_end() { return t("Follower (Monatsende)", "Followers (end of month)"); },
  get stories() { return t("Story-Aufrufe", "Story views"); }, get profile_clicks() { return t("Klicks im Profil", "Profile clicks"); }, get follower_split() { return t("Anteil Follower", "Follower share"); }, get content_types() { return t("Formate", "Formats"); }, get engagement_rate() { return t("Engagement-Rate", "Engagement rate"); },
};
export function metricLabel(key: string): string {
  return METRIC[key] ?? blockLabel(key);
}

export interface IgData {
  account: IgAccountRow | null;
  periodTotals: IgPeriodRow[];
  audience: IgAudienceRow[];
  monthly: IgMonthlyRow[];
  media: IgMediaRow[];
  consent: IgConsentRow | null;
  shares: IgShareRow[];
  views: IgShareViewRow[];
  /** 0340 */
  daily?: IgDailyRow[];
  stories?: IgStoryRow[];
}
export type ShareState = "active" | "expired" | "revoked" | "locked";
export function shareState(s: Pick<IgShareRow, "revoked_at" | "locked_at" | "expires_at">, nowMs: number): ShareState {
  if (s.revoked_at) return "revoked";
  if (s.locked_at) return "locked";
  return Date.parse(s.expires_at) <= nowMs ? "expired" : "active";
}
export function sharePill(s: ShareState): Pill {
  switch (s) {
    case "active": return pill(t("Aktiv", "Active"), "success");
    case "expired": return pill(t("Abgelaufen", "Expired"), "neutral");
    case "revoked": return pill(t("Zurückgezogen", "Withdrawn"), "neutral");
    case "locked": return pill(t("Gesperrt · 5 Fehlversuche", "Locked · 5 failed attempts"), "error");
  }
}
export function shareViewStats(views: readonly IgShareViewRow[], shareId: string): { ok: number; failed: number; last: string | null } {
  const list = views.filter((v) => v.share_id === shareId);
  const okList = list.filter((v) => v.ok).map((v) => v.at).sort();
  return { ok: okList.length, failed: list.length - okList.length, last: okList[okList.length - 1] ?? null };
}
/** Einwilligung des Creators (TA-139): was die Agentur weitergeben darf und was derzeit nicht freigegeben ist (ersetzt AD-200). */
export function consentInfo(c: IgConsentRow | null): { allowed: boolean; blocks: string[]; never: string[] } {
  const blocks = c?.sharing_allowed ? IG_BLOCKS.map((b) => b.key).filter((k) => c.allowed_blocks.includes(k)) : [];
  return { allowed: !!c?.sharing_allowed, blocks, never: IG_BLOCKS.map((b) => b.key).filter((k) => !blocks.includes(k)) };
}
/** Ablaufdatum → Gültigkeit in Tagen für ig_share_create (1 bis 90). */
export function daysUntil(dateIso: string, today: string): number {
  return diffDays(today, dateIso);
}

export interface SharePreview {
  profile: { username: string; name: string | null; followers: number | null; mediaCount: number | null } | null;
  totals: { key: string; value: number | null }[];
  totalsOn: string | null;
  totalsPeriod: string;
  audience: { breakdown: string; label: string; entries: { key: string; share: number }[] }[];
  monthly: { month: string; metrics: Metrics }[] | null;
  topPosts: IgMediaRow[] | null;
}
const AUDIENCE: Record<string, { breakdown: string; label: string }> = {
  age: { breakdown: "age", get label() { return t("Altersgruppen", "Age groups"); } }, gender: { breakdown: "gender", get label() { return t("Geschlecht", "Gender"); } },
  countries: { breakdown: "country", get label() { return t("Länder", "Countries"); } }, cities: { breakdown: "city", get label() { return t("Städte", "Cities"); } },
};
/** Was die Marke sieht (gleiche Auswahl wie ig_share_open, aber ohne einen Aufruf zu zählen; AD-195/AD-198). */
export function sharePreview(ig: IgData, requested: readonly string[], period: string, today: string): SharePreview {
  // Gleiche Regel wie ig_share_open: nur Blöcke, die die Einwilligung von heute noch erlaubt
  const allowed = consentInfo(ig.consent).blocks;
  const blocks = requested.filter((k) => allowed.includes(k));
  const has = (k: string) => blocks.includes(k);
  const totalsPeriod = period === "12m" || period === "all" ? "90d" : period;
  const latest = ig.periodTotals.filter((t) => t.period === totalsPeriod).sort((a, b) => b.computed_on.localeCompare(a.computed_on))[0] ?? null;
  const totals = latest ? Object.entries(latest.metrics).filter(([k]) => has(k)).map(([key, value]) => ({ key, value })) : [];
  const audience = Object.entries(AUDIENCE)
    .filter(([block]) => has(block))
    .map(([, a]) => {
      const row = ig.audience.filter((x) => x.breakdown === a.breakdown).sort((x, y) => y.captured_on.localeCompare(x.captured_on))[0];
      const entries = row ? Object.entries(row.data).filter((e): e is [string, number] => typeof e[1] === "number").map(([key, share]) => ({ key, share })).sort((x, y) => y.share - x.share).slice(0, 5) : [];
      return { breakdown: a.breakdown, label: a.label, entries };
    });
  const cutoff = `${addMonths(today.slice(0, 7), -12)}-01`;
  const monthly = period === "12m" || period === "all"
    ? ig.monthly.filter((m) => period === "all" || m.month >= cutoff).sort((a, b) => a.month.localeCompare(b.month)).map((m) => ({
        month: m.month.slice(0, 7),
        metrics: Object.fromEntries(Object.entries(m.metrics).filter(([k]) => has(k) || ((k === "followers" || k === "followers_end") && has("profile")))),
      }))
    : null;
  const topPosts = has("top_posts")
    ? [...ig.media].sort((a, b) => (b.metrics.views ?? -1) - (a.metrics.views ?? -1)).slice(0, 6)
    : null;
  return {
    profile: has("profile") && ig.account ? { username: ig.account.username, name: ig.account.name, followers: ig.account.followers_count, mediaCount: ig.account.media_count } : null,
    totals, totalsOn: latest?.computed_on ?? null, totalsPeriod, audience, monthly, topPosts,
  };
}
/**
 * Vorschau genau wie bei der Marke: dieselbe Antwort wie app.ig_share_payload (0042), gebaut aus den Daten, die die Agentur
 * ohnehin sieht, und angezeigt mit derselben Ansicht (buildView + InsightsAnsicht). So zeigt die Vorschau Anteile, Länder,
 * Formate und Beitragsarten gleich benannt wie die Marke und zählt keinen Aufruf.
 */
export function previewPayload(ig: IgData, requested: readonly string[], period: string, today: string,
  meta: { agency: string | null; talentName: string | null; expiresAt: string | null }): ShareResponse {
  const allowed = consentInfo(ig.consent).blocks;
  const blocks = requested.filter((k) => allowed.includes(k));
  const has = (k: string) => blocks.includes(k);
  const totalsPeriod = period === "12m" || period === "all" ? "90d" : period;
  const latest = ig.periodTotals.filter((t) => t.period === totalsPeriod).sort((a, b) => b.computed_on.localeCompare(a.computed_on))[0] ?? null;
  const audience: Record<string, unknown> = {};
  let timeframe: string | null = null;
  for (const [block, a] of Object.entries(AUDIENCE)) {
    if (!has(block)) continue;
    const row = ig.audience.filter((x) => x.breakdown === a.breakdown).sort((x, y) => y.captured_on.localeCompare(x.captured_on))[0];
    if (row) audience[a.breakdown] = row.data;
    if (row?.timeframe && !timeframe) timeframe = row.timeframe;
  }
  const cutoff = `${addMonths(today.slice(0, 7), -12)}-01`;
  // 0340: wie app.ig_share_payload – Vergleich nur aus vollständigen Tageswerten, Storys des Zeitraums nur mit eigenem Block
  const storyDays = totalsPeriod === "7d" ? 7 : totalsPeriod === "30d" ? 30 : 90;
  const storyFrom = new Date(Date.parse(`${today}T00:00:00Z`) - (storyDays - 1) * 86_400_000).toISOString();
  return {
    compare: period === "7d" || period === "30d" ? compareFromDaily((ig.daily ?? []).map((d) => ({ day: d.day, metrics: d.metrics })), period, latest?.computed_on ?? null, blocks) : null,
    audience_timeframe: timeframe,
    stories: has("story_items")
      ? (ig.stories ?? []).filter((s) => s.posted_at >= storyFrom).sort((a, b) => b.posted_at.localeCompare(a.posted_at)).slice(0, 30)
          .map((s) => ({ posted_at: s.posted_at, type: s.media_type, metrics: s.metrics, measured_at: s.measured_at, measurements: s.measurements, few_viewers: s.few_viewers }))
      : null,
    ok: true, blocks, period, agency: meta.agency, talent_name: meta.talentName, expires_at: meta.expiresAt, as_of: latest?.computed_on ?? null,
    profile: has("profile") && ig.account
      ? { username: ig.account.username, name: ig.account.name, followers: ig.account.followers_count, media_count: ig.account.media_count } : null,
    period_totals: latest ? Object.fromEntries(Object.entries(latest.metrics).filter(([k]) => has(k))) : null,
    audience,
    monthly: period === "12m" || period === "all"
      ? ig.monthly.filter((m) => period === "all" || m.month >= cutoff).sort((a, b) => a.month.localeCompare(b.month)).map((m) => ({ month: m.month, metrics: m.metrics }))
      : null,
    top_posts: has("top_posts")
      ? [...ig.media].sort((a, b) => (b.metrics.views ?? -1) - (a.metrics.views ?? -1)).slice(0, 6)
          .map((m) => ({ permalink: m.permalink, type: m.media_type, posted_at: m.posted_at, metrics: m.metrics, thumb: m.thumbnail_path ?? null }))
      : null,
  };
}
/** 0340: Antwort von ig_share_send_preview (Probelauf, schreibt nichts) */
export interface ShareMailPreview {
  share_id: string; recipient: string; language: string; agency: string | null; locale: string | null;
  subject: string | null; body: string | null; sent_count: number; params: Record<string, unknown>;
}
/**
 * Probelauf: die Mail so, wie der Postausgang sie aus Vorlage und Werten baut (dieselben Bausteine aus @talentkontor/domain),
 * als Klartext. Der Link entsteht erst beim Senden; hier steht ein Platzhalter. Die persönliche Nachricht folgt abgesetzt wie in
 * der Mail (packages/server outbox.ts, withPersonalMessage).
 */
export function renderShareMail(p: ShareMailPreview, origin: string): { recipient: string; subject: string; text: string; unresolved: string[] } {
  const lang = p.language === "en" ? "en" : "de";
  const link = `${origin.replace(/\/+$/, "")}/i/${lang === "en" ? "…?l=en" : "…"}`;
  const vars = mailVariables(p.params ?? {}, { agencyName: p.agency ?? "", locale: lang === "en" ? "en-GB" : ((p.locale as "de-DE" | null) ?? "de-DE"), language: lang, link });
  const mail = renderMailText({ subject: p.subject ?? "", body: p.body ?? "" }, vars);
  const message = typeof p.params?.message === "string" && p.params.message.trim() ? p.params.message.trim() : null;
  let html = mail.html;
  if (message) {
    // Wie withPersonalMessage im Postausgang: abgesetzt nach dem Absatz mit dem Link (ohne Link nach dem zweiten Absatz)
    const esc = (x: string) => x.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const intro = lang === "en" ? `Message from ${esc(p.agency ?? "")}:` : `Nachricht von ${esc(p.agency ?? "")}:`;
    const block = `<p><strong>${intro}</strong><br>${esc(message.slice(0, 2000)).replace(/\r?\n/g, "<br>")}</p>`;  // Markup der Mail (Sprache folgt der Mail), Eintrag in sprache-ausnahmen.json
    const ends: number[] = [];
    for (let i = html.indexOf("</p>"); i >= 0; i = html.indexOf("</p>", i + 4)) ends.push(i + 4);
    const linkAt = html.search(/<p[^>]*>\s*<a\s[^>]*href=/i);
    const pos = (linkAt >= 0 ? ends.find((e) => e > linkAt) : undefined) ?? ends[1] ?? ends[0];
    html = pos === undefined ? block + html : html.slice(0, pos) + block + html.slice(pos);
  }
  return { recipient: p.recipient, subject: mail.subject, text: mailHtmlToPlainText(html), unresolved: mail.unresolved };
}
export function igStatusPill(a: IgAccountRow | null): Pill {
  if (!a) return pill(t("Nicht verbunden", "Not connected"), "neutral");
  if (a.status === "active") return pill(t("Verbunden", "Connected"), "success");
  if (a.status === "expired") return pill(t("Anmeldung abgelaufen", "Sign-in expired"), "warning");
  return pill(t("Getrennt", "Disconnected"), "neutral");
}

// ── Zusammensetzen je Seite ──────────────────────────────────────────────────────────────────────

export interface RawBase {
  today: string;
  nowMs: number;
  currency: string;
  settings: OrgSettingsRow;
  cfg: OrgConfig;
  talents: TalentFull[];
  brands: BrandFull[];
  deals: DealFull[];
  periods: PeriodRow[];
  extras: LineItemRow[];
  terms: AgencyTermsRow[];
  invoices: InvoiceRow[];
  bookings: BookingRow[];
  links: LinkRow[];
  invitations: InvitationRow[];
  contracts: ContractRow[];
  presence: PresenceRow[];
  /** Wechselkurse je Monat (fx_rates_monthly) für Umsätze in anderer Währung (SAAS-R2-1) */
  fx?: FxRateRow[];
}
export interface Base extends RawBase {
  views: InvoiceView[];
  occ: Occurrence[];
  unconverted: number;
  talentById: Map<string, TalentFull>;
  brandById: Map<string, BrandFull>;
  dealById: Map<string, DealFull>;
  lines: DealLine[];
}
export function prepareBase(raw: RawBase): Base {
  const views = makeViews(raw.invoices, { cfg: raw.cfg, today: raw.today, holidayRegion: holidayRegionOf(raw.settings), periods: raw.periods });
  const { occ, unconverted } = occurrencesOf(raw);
  const talentById = new Map(raw.talents.map((t) => [t.id, t]));
  const brandById = new Map(raw.brands.map((b) => [b.id, b]));
  const lines = dealLines({
    deals: raw.deals, periods: raw.periods, extras: raw.extras, views, today: raw.today,
    talentName: (id) => nameOf(talentById.get(id)), brandName: (id) => brandById.get(id)?.name ?? "–",
  });
  return { ...raw, views, occ, unconverted, talentById, brandById, dealById: new Map(raw.deals.map((d) => [d.id, d])), lines };
}

/**
 * Anzeige-Pillen eines Creators: der Status nur, wenn er etwas anderes sagt als der Zugang (kein „Eingeladen“ doppelt, und kein
 * „Eingeladen“ neben „Einladung abgelaufen“).
 */
export function statusPills(t: Pick<TalentFull, "status">, access: Access): Pill[] {
  if (t.status === "archived") return [talentStatusPill("archived")];
  if (t.status === "invited" || access.state === "invited") return [access.state === "none" ? talentStatusPill("invited") : access.pill];
  return [talentStatusPill(t.status), access.pill];
}

export type CreatorFilter = "alle" | "aktiv" | "eingeladen" | "ohne_zugang" | "archiviert" | "meine";
export interface CreatorLine {
  talent: TalentFull;
  name: string;
  statusPill: Pill;
  access: Access;
  commissionBp: number;
  commissionIsDefault: boolean;
  activeDeals: number;
  deals: number;
  yearGross: number;
  lifetimeAgency: number;
  overdue: number;
  /** Zuständige Manager (manager_talent_assignments); leer = niemand fest zugewiesen */
  managers: string[];
  mine: boolean;
}
export interface TeamInfo { members: { id: string; user_id: string; name: string; role?: string }[]; assignments: { membership_id: string; talent_id: string }[]; selfId: string | null }
export function managersOf(team: TeamInfo | null | undefined, talentId: string): string[] {
  if (!team) return [];
  const ids = new Set(team.assignments.filter((a) => a.talent_id === talentId).map((a) => a.membership_id));
  return team.members.filter((m) => ids.has(m.id)).map((m) => m.name).sort((a, b) => a.localeCompare(b, "de"));
}
export function creatorLines(b: Base, team?: TeamInfo | null): CreatorLine[] {
  const def = b.settings.default_commission_bp ?? 0;
  return b.talents.map((t) => {
    const occ = b.occ.filter((o) => o.talentId === t.id);
    const lines = b.lines.filter((l) => l.deal.talent_id === t.id);
    return {
      talent: t, name: nameOf(t), statusPill: talentStatusPill(t.status), access: accessOf(t.id, b.links, b.invitations, b.nowMs),
      commissionBp: t.commission_bp ?? def, commissionIsDefault: t.commission_bp == null,
      activeDeals: lines.filter((l) => l.active).length, deals: lines.length,
      yearGross: totalsOf(inYear(occ, b.today)).gross, lifetimeAgency: totalsOf(upToNow(occ, b.today)).agency,
      overdue: overdueInfo(invoicesOfTalent(b.views, t.id))?.count ?? 0,
      managers: managersOf(team, t.id),
      mine: !!team?.selfId && team.assignments.some((a) => a.talent_id === t.id && a.membership_id === team.selfId),
    };
  }).sort((a, x) => a.name.localeCompare(x.name, "de"));
}
/**
 * Lässt sich der Creator zum Portal einladen (Sammel-Einladung, PRAXIS-8)? Nicht archiviert, noch kein Zugang und keine
 * offene Einladung, gültige E-Mail-Adresse (gleiche Regeln wie invitation_create).
 */
export function invitable(l: Pick<CreatorLine, "talent" | "access">): boolean {
  return l.talent.status !== "archived" && l.access.state !== "linked" && l.access.state !== "invited" && emailValid((l.talent.email ?? "").trim());
}
export function matchesCreatorFilter(l: CreatorLine, f: CreatorFilter): boolean {
  switch (f) {
    case "aktiv": return l.talent.status === "active";
    case "eingeladen": return l.talent.status !== "archived" && (l.talent.status === "invited" || l.access.state === "invited");
    case "ohne_zugang": return l.talent.status !== "archived" && l.access.state !== "linked" && l.access.state !== "invited";
    case "archiviert": return l.talent.status === "archived";
    case "meine": return l.talent.status !== "archived" && l.mine;
    default: return l.talent.status !== "archived";
  }
}

export interface TalentProfile {
  talent: TalentFull;
  name: string;
  statusPill: Pill;
  access: Access;
  presence: PresenceRow | null;
  commissionBp: number;
  commissionIsDefault: boolean;
  lifetime: RevenueTotals;
  since: string | null;
  year: RevenueTotals;
  planned: RevenueTotals;
  series: MonthPoint[];
  topBrands: Rank[];
  deals: DealLine[];
  activeDeals: number;
  brandCount: number;
  invoices: InvoiceView[];
  open: OpenInfo;
  payouts: OpenInfo & { inReview: number };
  overdue: OverdueInfo | null;
  contracts: ContractRow[];
  bookings: BookingRow[];
  flatTax: FlatTaxLine[];
}
export function talentProfile(b: Base, talentId: string): TalentProfile | null {
  const t = b.talentById.get(talentId);
  if (!t) return null;
  const occ = b.occ.filter((o) => o.talentId === talentId);
  const past = upToNow(occ, b.today);
  const deals = b.lines.filter((l) => l.deal.talent_id === talentId);
  const invoices = invoicesOfTalent(b.views, talentId);
  const def = b.settings.default_commission_bp ?? 0;
  const brandName = (id: string) => b.brandById.get(id)?.name ?? "–";
  return {
    talent: t, name: nameOf(t), statusPill: talentStatusPill(t.status), access: accessOf(t.id, b.links, b.invitations, b.nowMs),
    presence: b.presence.find((p) => p.talent_id === talentId) ?? null,
    commissionBp: t.commission_bp ?? def, commissionIsDefault: t.commission_bp == null,
    lifetime: totalsOf(past), since: past.map((o) => o.month).sort()[0] ?? null,
    year: totalsOf(inYear(occ, b.today)), planned: totalsOf(planned(occ, b.today)),
    series: monthlySeries(occ, b.today), topBrands: rankBy(past, "brandId"),
    deals, activeDeals: deals.filter((l) => l.active).length, brandCount: new Set(deals.map((l) => l.deal.brand_id)).size,
    invoices, open: openInfo(invoices), payouts: payoutInfo(invoices), overdue: overdueInfo(invoices),
    contracts: b.contracts.filter((c) => c.talent_id === talentId).sort((x, y) => y.created_at.localeCompare(x.created_at)),
    bookings: b.bookings.filter((x) => x.talent_id === talentId).sort((x, y) => y.booked_on.localeCompare(x.booked_on)),
    flatTax: flatTaxLines(deals.map((l) => l.deal), b.periods, brandName),
  };
}

export type BrandFilter = "alle" | "aktiv" | "ohne" | "ueberfaellig" | "agenturen";
export interface BrandLine {
  brand: BrandFull;
  statusPill: Pill;
  contact: string | null;
  agencyName: string | null;
  deals: number;
  activeDeals: number;
  yearGross: number;
  lifetimeAgency: number;
  open: OpenInfo;
  overdue: OverdueInfo | null;
  last: string | null;
  /** Agentur-Eintrag: Zahlen der betreuten Marken, getrennt ausgewiesen (zählen bei den Marken selbst) */
  via: { brands: number; deals: number; activeDeals: number; open: OpenInfo } | null;
}
function brandState(brand: BrandFull, deals: number, active: number): Pill {
  if (brand.kind === "agency") return pill(t("Agentur", "Agency"), "info");
  if (active > 0) return pill(t("Aktiv", "Active"), "success");
  if (deals > 0) return pill(t("Ruhend", "Dormant"), "neutral");
  return pill(t("Ohne Kooperation", "Without a deal"), "neutral");
}
export function brandContact(brand: Pick<BrandFull, "id" | "invoice_email">, contacts: readonly ContactRow[]): string | null {
  const own = contacts.filter((c) => c.brand_id === brand.id);
  return brand.invoice_email ?? own.find((c) => c.is_invoice_recipient && c.email)?.email ?? own.find((c) => c.email)?.email ?? own[0]?.name ?? null;
}
/** Kennzahlen der Marken, die ein Agentur-Eintrag betreut (Liste und Detail rechnen gleich). */
export function viaAgency(b: Base, agencyId: string): { brandIds: string[]; lines: DealLine[]; invoices: InvoiceView[]; occ: Occurrence[] } {
  const brandIds = b.brands.filter((x) => x.represented_by === agencyId).map((x) => x.id);
  const ids = new Set(brandIds);
  return {
    brandIds,
    lines: b.lines.filter((l) => ids.has(l.deal.brand_id)),
    invoices: brandIds.flatMap((id) => invoicesOfBrand(b.views, b.dealById, id)),
    occ: b.occ.filter((o) => !!o.brandId && ids.has(o.brandId)),
  };
}
/**
 * Marken-Liste: jede Zeile zählt nur ihre eigenen Kooperationen und Rechnungen. Bei Agentur-Einträgen stehen die Zahlen der
 * betreuten Marken getrennt daneben (sie zählen schon bei den Marken; Summen über die Liste bleiben ohne Doppelzählung).
 */
export function brandLines(b: Base, contacts: readonly ContactRow[]): BrandLine[] {
  return b.brands.map((brand) => {
    const occ = b.occ.filter((o) => o.brandId === brand.id);
    const lines = b.lines.filter((l) => l.deal.brand_id === brand.id);
    const invoices = invoicesOfBrand(b.views, b.dealById, brand.id);
    const { last } = lastAndNext(lines.map((l) => l.deal), b.periods, b.today);
    const via = brand.kind === "agency" ? viaAgency(b, brand.id) : null;
    return {
      brand, statusPill: brandState(brand, lines.length, lines.filter((l) => l.active).length), contact: brandContact(brand, contacts),
      agencyName: brand.represented_by ? (b.brandById.get(brand.represented_by)?.name ?? null) : null,
      deals: lines.length, activeDeals: lines.filter((l) => l.active).length,
      yearGross: totalsOf(inYear(occ, b.today)).gross, lifetimeAgency: totalsOf(upToNow(occ, b.today)).agency,
      open: openInfo(invoices), overdue: overdueInfo(invoices), last,
      via: via ? { brands: via.brandIds.length, deals: via.lines.length, activeDeals: via.lines.filter((l) => l.active).length, open: openInfo(via.invoices) } : null,
    };
  }).sort((x, y) => x.brand.name.localeCompare(y.brand.name, "de"));
}
export function matchesBrandFilter(l: BrandLine, f: BrandFilter): boolean {
  switch (f) {
    case "aktiv": return l.activeDeals > 0;
    case "ohne": return l.deals === 0 && l.brand.kind === "brand";
    case "ueberfaellig": return !!l.overdue;
    case "agenturen": return l.brand.kind === "agency";
    default: return true;
  }
}

export interface BrandProfile {
  brand: BrandFull;
  statusPill: Pill;
  agency: BrandFull | null;
  represented: BrandFull[];
  lifetime: RevenueTotals;
  year: RevenueTotals;
  avgDeal: number;
  deals: DealLine[];
  activeDeals: number;
  talentCount: number;
  talents: Rank[];
  last: string | null;
  next: string | null;
  followUp: ReturnType<typeof followUp>;
  invoices: InvoiceView[];
  open: OpenInfo;
  overdue: OverdueInfo | null;
  contracts: ContractRow[];
  bookings: BookingRow[];
  /** Agentur-Eintrag: Kooperationen, Rechnungen und Umsatz der betreuten Marken (getrennt von den eigenen Zahlen) */
  via: { deals: DealLine[]; invoices: InvoiceView[]; open: OpenInfo; overdue: OverdueInfo | null; year: RevenueTotals; lifetime: RevenueTotals } | null;
  /** Was für Rechnungen an die Marke fehlt (gleiche Regeln wie die Rechnungsprüfung: Name, Anschrift, Land, Mail, USt-IdNr.) */
  gaps: string[];
}
export function brandProfile(b: Base, brandId: string): BrandProfile | null {
  const brand = b.brandById.get(brandId);
  if (!brand) return null;
  const occ = b.occ.filter((o) => o.brandId === brandId);
  const past = upToNow(occ, b.today);
  const deals = b.lines.filter((l) => l.deal.brand_id === brandId);
  const invoices = invoicesOfBrand(b.views, b.dealById, brandId);
  const lifetime = totalsOf(past);
  const { last, next } = lastAndNext(deals.map((l) => l.deal), b.periods, b.today);
  return {
    brand, statusPill: brandState(brand, deals.length, deals.filter((l) => l.active).length),
    agency: brand.represented_by ? (b.brandById.get(brand.represented_by) ?? null) : null,
    represented: b.brands.filter((x) => x.represented_by === brandId).sort((x, y) => x.name.localeCompare(y.name, "de")),
    lifetime, year: totalsOf(inYear(occ, b.today)), avgDeal: lifetime.deals ? Math.round(lifetime.gross / lifetime.deals) : 0,
    deals, activeDeals: deals.filter((l) => l.active).length, talentCount: new Set(deals.map((l) => l.deal.talent_id)).size,
    talents: rankBy(past, "talentId"), last, next, followUp: followUp(last, next, b.today),
    invoices, open: openInfo(invoices), overdue: overdueInfo(invoices),
    contracts: b.contracts.filter((c) => c.brand_id === brandId).sort((x, y) => y.created_at.localeCompare(x.created_at)),
    bookings: b.bookings.filter((x) => x.brand_id === brandId || (!!x.deal_id && b.dealById.get(x.deal_id)?.brand_id === brandId)).sort((x, y) => y.booked_on.localeCompare(x.booked_on)),
    via: brand.kind === "agency" ? (() => {
      const v = viaAgency(b, brandId);
      return { deals: v.lines, invoices: v.invoices, open: openInfo(v.invoices), overdue: overdueInfo(v.invoices), year: totalsOf(inYear(v.occ, b.today)), lifetime: totalsOf(upToNow(v.occ, b.today)) };
    })() : null,
    gaps: brandGaps(brand, (b.settings.country as string | null | undefined) ?? null, []),
  };
}

/** Was für Rechnungen an eine Marke fehlt (Hinweis auf der Markenseite; die Rechnungsprüfung nutzt dieselben Punkte). */
export function brandGaps(brand: Pick<BrandFull, "id" | "legal_name" | "name" | "street" | "postal_code" | "city" | "country" | "vat_id" | "invoice_email" | "is_business" | "kind">,
  orgCountry: string | null, contacts: readonly ContactRow[]): string[] {
  const gaps: string[] = [];
  if (!cleanText(brand.legal_name)) gaps.push(t("rechtlicher Firmenname", "legal company name"));
  if (!cleanText(brand.street) || !cleanText(brand.postal_code) || !cleanText(brand.city)) gaps.push(t("Anschrift", "Address"));
  if (!cleanText(brand.country)) gaps.push(t("Land", "Country"));
  if (!cleanText(brand.invoice_email) && !contacts.some((c) => c.brand_id === brand.id && c.email && contactRoles(c).includes("invoice"))) gaps.push(t("Rechnungs-E-Mail", "Invoice email"));
  if (brand.is_business && vatRequired(brand.country, orgCountry) && !cleanText(brand.vat_id)) gaps.push(t("USt-IdNr. (Reverse Charge)", "VAT ID (reverse charge)"));
  return gaps;
}

/** Was an einem Creator bzw. einer Marke hängt (Löschen nur ohne Belege, AD-078/AD-086). */
export function usageOfTalent(b: Base, talentId: string): { deals: number; invoices: number; contracts: number; bookings: number } {
  return {
    deals: b.deals.filter((d) => d.talent_id === talentId).length,
    invoices: b.invoices.filter((i) => i.talent_id === talentId).length,
    contracts: b.contracts.filter((c) => c.talent_id === talentId).length,
    bookings: b.bookings.filter((x) => x.talent_id === talentId).length,
  };
}
export function usageOfBrand(b: Base, brandId: string): { deals: number; invoices: number; contracts: number; bookings: number } {
  return {
    deals: b.deals.filter((d) => d.brand_id === brandId).length,
    invoices: b.invoices.filter((i) => i.brand_id === brandId).length,
    contracts: b.contracts.filter((c) => c.brand_id === brandId).length,
    bookings: b.bookings.filter((x) => x.brand_id === brandId).length,
  };
}
export function usageTotal(u: { deals: number; invoices: number; contracts: number; bookings: number }): number {
  return u.deals + u.invoices + u.contracts + u.bookings;
}

/** Pflichtangaben im Creator-Profil (platform_defaults.talent_profile_requirements, 0006), wenn die Agentur keine eigenen setzt. */
export const DEFAULT_PROFILE_REQUIREMENTS = ["street", "postal_code", "city", "country", "iban", "bank_account_holder"];
const PROFILE_FIELD: Record<string, ProfileField> = {
  street: "street", postal_code: "postalCode", city: "city", country: "country", iban: "iban", bank_account_holder: "accountHolder", bic: "bic", bank_name: "bankName",
};
const GAP_LABEL: Record<string, string> = {
  get street() { return t("Straße", "Street"); }, postalCode: "PLZ", get city() { return t("Ort", "City"); }, get country() { return t("Land", "Country"); }, get "country:unknown"() { return t("Land (unbekannt)", "Country (unknown)"); }, iban: "IBAN", get accountHolder() { return t("Kontoinhaber", "Account holder"); },
  bic: "BIC", bankName: "Bank", get issuerTaxNumberOrVatId() { return t("Steuernummer oder USt-IdNr.", "Tax number or VAT ID"); }, get issuerVatId() { return t("UID-Nummer (USt-IdNr.)", "UID number (VAT ID)"); }, get issuerChUid() { return t("UID-Nummer (MWST)", "UID number (VAT)"); },
};
/**
 * ST-22: Was für Rechnungen im Profil fehlt – mit derselben Regel wie das Creator-Portal (checkIssuerProfile: Pflichtfelder der
 * Agentur, Steuernummer je Land, z. B. UID in Österreich, CH-UID in der Schweiz). Bankangaben je Auszahlungsweg: IBAN im SEPA-Raum
 * (BIC nur außerhalb), Kontonummer bei SWIFT, E-Mail bei PayPal und Wise. Bankdaten nur, wenn sie sichtbar sind (undefined = kein Recht).
 */
export function profileGaps(tl: TalentFull, payout: PayoutRow | null | undefined, requirements: readonly string[] = DEFAULT_PROFILE_REQUIREMENTS): string[] {
  const bankKeys = ["iban", "bank_account_holder", "bic", "bank_name"];
  const fields = requirements.filter((k) => !bankKeys.includes(k)).map((k) => PROFILE_FIELD[k]).filter((x): x is ProfileField => !!x);
  const r = checkIssuerProfile({ street: tl.street, postalCode: tl.postal_code, city: tl.city, country: tl.country, smallBusiness: tl.small_business, vatId: tl.vat_id, taxNumber: tl.tax_number }, fields);
  const gaps = r.missing.map((m) => GAP_LABEL[m] ?? m);
  if (payout !== undefined) {
    const method = payout?.payout_method ?? "sepa";
    if (method === "paypal" || method === "wise") {
      if (!payout?.payout_email) gaps.push(method === "paypal" ? t("PayPal-Adresse", "PayPal address") : t("Wise-Adresse", "Wise address"));
    } else {
      if (requirements.includes("bank_account_holder") && !payout?.bank_account_holder) gaps.push(t("Kontoinhaber", "Account holder"));
      if (method === "swift") {
        if (!payout?.iban && !payout?.account_number) gaps.push(t("IBAN oder Kontonummer", "IBAN or account number"));
        if (!payout?.bic) gaps.push("BIC (SWIFT)");
      } else {
        if (requirements.includes("iban") && !payout?.iban) gaps.push("IBAN");
        // BIC ist im SEPA-Raum entbehrlich; Pflicht nur, wenn die Agentur sie ausdrücklich verlangt oder das Konto außerhalb liegt
        if (!payout?.bic && (requirements.includes("bic") || (payout?.bank_country && !inSepa(payout.bank_country)))) gaps.push("BIC");
      }
      if (requirements.includes("bank_name") && !payout?.bank_name) gaps.push("Bank");
    }
  }
  return [...new Set(gaps)];
}
export function latestTotals(rows: readonly IgPeriodRow[], period: string): IgPeriodRow | null {
  return rows.filter((r) => r.period === period).sort((a, b) => b.computed_on.localeCompare(a.computed_on))[0] ?? null;
}
export function formatCount(n: number | null | undefined): string {
  return n == null ? "–" : new Intl.NumberFormat(numberLocale()).format(n);
}

// ── Creator-Belegung im Tarif (K-592) ─────────────────────────────────────────

/**
 * Aktive Creator, wie die Tarifgrenze sie zählt (app.enforce_limit_talents, org_entitlements): nicht archiviert und keine
 * Beispieldaten (0325 is_sample). Der Hinweis zu den Beispieldaten sagt „zählen nicht in deinen Tarif“; die Fußzeile muss dazu passen.
 */
export function aktiveCreatorImTarif(talents: readonly { status: string; is_sample?: boolean | null }[]): number {
  return talents.filter((x) => x.status !== "archived" && x.is_sample !== true).length;
}

/**
 * Satz zur Belegung, auf Creator-Liste und Einladeseite gleich (K-592): im Test „Test im vollen Umfang: 1 von 60 aktiven Creator
 * belegt.“, sonst „Tarif Team: 1 von 20 aktiven Creator belegt.“
 */
export function creatorBelegungText(plan: { planName: string; maxTalents: number; testBis?: string | null }, aktiv: number): string {
  const max = plan.maxTalents < 0 ? t("unbegrenzt vielen", "unlimited") : String(plan.maxTalents);
  return plan.testBis
    ? t(`Test im vollen Umfang: ${aktiv} von ${max} aktiven Creator belegt.`, `Full-scope trial: ${aktiv} of ${max} active creators in use.`)
    : t(`Tarif ${plan.planName}: ${aktiv} von ${max} aktiven Creator belegt.`, `Plan ${plan.planName}: ${aktiv} of ${max} active creators in use.`);
}
